import {
	QUERYPARAM_MODELSTATEID,
	QUERYPARAM_SETTINGSURL,
	QUERYPARAM_SLUG,
} from "@AppBuilderShared/shared/config/queryparams";
import {IAppBuilderUrlBuilderData} from "@AppBuilderShared/shared/lib/urlbuilder";
import {
	IAddItemToCartData,
	IAddItemToCartReply,
	IECommerceApiActions,
	IGetParentPageInfoReply,
	IGetUserProfileReply,
	IMessageToParentData,
	IMessageToParentReply,
	IScrollingApiLoadMoreData,
	IScrollingApiLoadMoreReply,
	IScrollingApiSetParametersData,
	IScrollingApiSetParametersReply,
	IUpdateSharingLinkData,
	IUpdateSharingLinkReply,
} from "./shared/features/ecommerce/config/ecommerceapi";
import {IScrollingApiItemTypeSelect} from "./shared/features/ecommerce/config/scrollingapi";

// ============================================================================
// DEFAULT — TYPE DEFINITIONS
// ============================================================================

interface ICategory {
	children: ICategory[];
	id: number;
	name: string;
	path: string;
	slug: string;
	thumbnail_url: string;
}

interface IProduct {
	category_id: number;
	category_name: string;
	category_structure: Array<string>;
	description: string;
	downloadable_files: {
		height: number;
		image_size: string;
		keep_horizontal_orientation: boolean;
		keep_vertical_orientation: boolean;
		parameterValues: {
			file_url: string;
		};
		sessionId: Array<string>;
		width: number;
	}[];
	graphic_id: number;
	graphic_name: string;
	tags: string[];
	thumbnail_url?: string;
	image_url?: string;
}

interface IGraphicsApiResponse {
	categories?: ICategory[];
	products?: IProduct[];
}

// ============================================================================
// DEFAULT — MODULE-LEVEL VARIABLES & FUNCTIONS (Graphics API)
// ============================================================================

function getGraphicsApiQueryString(
	categories?: string[] | string,
	tags?: string,
	search?: string,
	categoryIds?: number[],
): string {
	const params = new URLSearchParams();
	if (Array.isArray(categories) && categories.length === 0)
		categories = undefined;

	if (!categories && !tags && !search) categories = "all";

	if (categoryIds && categoryIds.length > 0) {
		params.set(
			"category_id",
			categoryIds[categoryIds.length - 1].toString(),
		);
		if (!tags && !search) {
			params.set("tags", "all");
		}
	} else if (categories) {
		if (Array.isArray(categories))
			params.set("categories", categories.join(","));
		else params.set("categories", categories);
		if (categories !== "all" && !tags && !search) {
			params.set("tags", "all");
		}
	}
	if (tags) params.set("tags", tags);
	if (search) params.set("search", search);

	return params.toString();
}

let latestQuery: string = getGraphicsApiQueryString("");
const allCategoriesQuery: string = getGraphicsApiQueryString();
let latestCategorySlugs: Array<string> = [];
let pageSize: number = 10;
let currentIndex: number = 0;
const cachedResults: Record<string, IGraphicsApiResponse> = {};
const categoryIdentifier = "\u200B\u2063";
let resetCacheCallback: (() => void) | null = null;

function clearCache() {
	latestQuery = getGraphicsApiQueryString();
	latestCategorySlugs = [];
	pageSize = 10;
	currentIndex = 0;
	for (const key in cachedResults) delete cachedResults[key];
	if (resetCacheCallback) resetCacheCallback();
}

let cachedNonce: string = "";
let nonceFetchedAt: number = 0;
const NONCE_TTL_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Resolve whether the current site is the network's main/source site
 * (where the shapediver-graphic-components REST route can be hit
 * directly, same-origin), or a dealer subsite that must go through the
 * gc_proxy AJAX action instead to avoid Cloudflare / cross-origin blocks
 * on a cross-domain fetch.
 *
 * `configuratorData.api_base_url` is localized from PHP as
 * get_site_url(get_main_site_id()) — i.e. it always resolves to the
 * main site's URL, regardless of which site's page this bundle happens
 * to be running on. When it matches the current origin, this IS the
 * main site.
 *
 * Fails open to "main site" (direct REST) if the config value is
 * missing, since that was the previous (hardcoded) behavior and is the
 * safer default on a same-origin install.
 */
function isMainSite(): boolean {
	const apiBaseUrl =
		(typeof configuratorData !== "undefined" && configuratorData?.api_base_url) || "";
	if (!apiBaseUrl) return true;
	try {
		return new URL(apiBaseUrl).origin === window.location.origin;
	} catch {
		return apiBaseUrl.replace(/\/$/, "") === window.location.origin;
	}
}

/**
 * Build the correct graphic-components catalog URL for whichever site
 * this bundle is currently running on:
 *   - Main site: same-origin REST call straight to the plugin's route.
 *   - Subsite:   same-origin admin-ajax.php?action=gc_proxy call, which
 *                the shapediver-graphic-components plugin proxies
 *                server-to-server to the main site's REST route (see
 *                Plugin::gc_proxy()), avoiding a cross-origin fetch that
 *                Cloudflare blocks.
 *
 * IMPORTANT: never hardcode a specific site's domain here — this must
 * resolve correctly on every subsite the bundle is deployed to, not
 * just whichever site it happened to be built/tested on.
 */
function buildGraphicsApiUrl(queryString: string): string {
	const origin = window.location.origin;
	if (isMainSite()) {
		return `${origin}/wp-json/shapediver/v1/graphic-components?${queryString}`;
	}
	return `${origin}/wp-admin/admin-ajax.php?action=gc_proxy&${queryString}`;
}

/**
 * Upload a base64 data-URI snapshot as real multipart FormData instead of
 * embedding it as a raw field in an application/x-www-form-urlencoded body.
 *
 * Sending a multi-hundred-KB (or several-MB) base64 string inline in a
 * urlencoded POST field is fragile: PHP's post_max_size, hosting-panel
 * form-field-length caps, and reverse-proxy/CDN body limits can silently
 * truncate or drop the *entire* $_POST array with no client-visible error,
 * which is what was happening to the "add to cart" snapshot. This mirrors
 * the multipart upload already used by the save-design flow (which does
 * not have that problem) and reuses the same server-side WebP conversion.
 *
 * Returns the resulting attachment's URL and ID, or null on failure — callers
 * should fall back to sending the raw base64 as before if this returns null,
 * so a network hiccup here never blocks add-to-cart entirely.
 */
async function uploadSnapshotForCart(
	dataUri: string,
	modelStateId: string,
): Promise<{url: string; attachmentId: number} | null> {
	try {
		const blobResponse = await fetch(dataUri);
		const blob = await blobResponse.blob();

		const fileName = `shapediver_cart_${modelStateId || "unsaved"}_${Date.now()}.png`;
		const file = new File([blob], fileName, {type: blob.type || "image/png"});

		const formData = new FormData();
		formData.append("file", file);
		formData.append("model_state_id", modelStateId || "");

		const apiUrl = `${window.location.origin}/wp-json/shapediver/v1/upload-configurator-image`;
		const uploadResponse = await fetch(apiUrl, {
			method: "POST",
			body: formData,
			credentials: "same-origin",
		});

		if (!uploadResponse.ok) {
			console.error("❌ uploadSnapshotForCart: HTTP", uploadResponse.status);
			return null;
		}

		const result = await uploadResponse.json();
		if (result?.success === false || !result?.data?.url || !result?.data?.attachment_id) {
			console.error("❌ uploadSnapshotForCart: unexpected response", result);
			return null;
		}

		return {url: result.data.url, attachmentId: result.data.attachment_id};
	} catch (error) {
		console.error("❌ uploadSnapshotForCart error:", error);
		return null;
	}
}

// ============================================================================
// VARIANT MATCHING (color_id / size_id / base size → WooCommerce variation)
// ============================================================================

/**
 * Per-product cache of the raw variant list returned by the
 * `get_product_variants_debug` AJAX action (see PHP: each entry has
 * variation_id, sku, attributes, price, regular_price, sale_price,
 * in_stock, setup_cost).
 */
const variantCache: Record<string, any[]> = {};

/**
 * Fetch (and cache) the full list of WooCommerce variations for a product,
 * including each variation's regular_price and per-variation setup cost
 * (attribute_pa_global-setting-cost). Used by fixed-price mode to resolve
 * the correct price for each physical item returned by wrapper_pricing.
 */
async function fetchProductVariants(productId: number | string): Promise<any[]> {
	const key = productId.toString();
	if (variantCache[key]) return variantCache[key];

	try {
		const response = await fetch(configuratorData.ajaxurl, {
			method: "POST",
			headers: {"Content-Type": "application/x-www-form-urlencoded"},
			credentials: "same-origin",
			body: new URLSearchParams({
				action: "get_product_variants_debug",
				product_id: key,
			}),
		});

		if (!response.ok) throw new Error(`HTTP ${response.status}`);

		const result = await response.json();
		const variants = result?.success ? (result.data?.variants || []) : [];
		variantCache[key] = variants;
		return variants;
	} catch (error) {
		console.error("❌ fetchProductVariants error:", error);
		variantCache[key] = [];
		return [];
	}
}

/** Clear the cached variant list for a product (call after edits/reloads if ever needed). */
function clearVariantCache(productId?: number | string): void {
	if (productId === undefined) {
		for (const key in variantCache) delete variantCache[key];
	} else {
		delete variantCache[productId.toString()];
	}
}

/**
 * Find an attribute key on a variant's `attributes` map by fuzzy-matching
 * on a substring, ignoring hyphens/underscores/case. WooCommerce variation
 * attribute keys look like "attribute_pa_global-color",
 * "attribute_pa_global-size", "attribute_pa_global-setting-cost",
 * "attribute_pa_base-sizes" — the exact taxonomy slug can vary by install,
 * so we match loosely rather than hardcoding one exact key string.
 *
 * `exclude` may be a single string or a list; any key containing one of
 * them (after normalisation) is skipped.
 */
function findAttrKey(
	attrs: Record<string, any>,
	needle: string,
	exclude?: string | string[],
): string | undefined {
	const target = needle.toLowerCase().replace(/[-_]/g, "");
	const excludes = (Array.isArray(exclude) ? exclude : exclude ? [exclude] : [])
		.map((e) => e.toLowerCase().replace(/[-_]/g, ""));
	return Object.keys(attrs).find((k) => {
		const norm = k.toLowerCase().replace(/[-_]/g, "");
		if (!norm.includes(target)) return false;
		if (excludes.some((ex) => norm.includes(ex))) return false;
		return true;
	});
}

/** Normalise a size string so "34x12x6", "34 X 12 X 6" and " 34x12x6 " compare equal. */
function normalizeSizeValue(value: any): string {
	return String(value ?? "").toLowerCase().replace(/\s+/g, "").trim();
}

/**
 * Find the "Base" piece among wrapper_pricing's items (name contains the
 * word "Base", e.g. "Base 2-10x1-0x0-6 PFT/BRP Dark Cloud Grey") and return
 * its `size` (e.g. "34x12x6"). This is matched against the variation's
 * `pa_base-sizes` attribute. Returns undefined if there is no base item.
 */
function findBaseItemSize(items: any[]): string | undefined {
	const baseItem = (items || []).find(
		(it) => typeof it?.name === "string" && /\bbase\b/i.test(it.name),
	);
	const size = baseItem?.size;
	return typeof size === "string" && size.trim() !== "" ? size.trim() : undefined;
}

/**
 * Match a wrapper_pricing API item (identified by its color_id / size_id,
 * e.g. '26_color' / '27_size') against the real WooCommerce variant list
 * for the product, returning the variation whose attribute values match.
 *
 * When the product has a Base piece, the base item's `size` (e.g.
 * "34x12x6") must also match the variation's `pa_base-sizes` attribute.
 * The base-size check is only enforced when a base size was found in the
 * API items AND the variant actually has a base-sizes attribute, so
 * products without a base keep matching on color + size exactly as before.
 *
 * Returns null if no variant matches.
 */
function matchVariantByColorSize(
	variants: any[],
	colorId: string | undefined,
	sizeId: string | undefined,
	baseSize?: string,
): any | null {
	if (!colorId || !sizeId) return null;

	const wantedBaseSize = baseSize ? normalizeSizeValue(baseSize) : "";

	for (const v of variants) {
		const attrs = v.attributes || {};

		const colorKey = findAttrKey(attrs, "color");
		// Exclude "setting" (pa_global-setting-cost) AND "base" (pa_base-sizes)
		// so this always resolves to pa_global_size.
		const sizeKey = findAttrKey(attrs, "size", ["setting", "base"]);
		const baseSizeKey = findAttrKey(attrs, "basesize");

		const variantColor = colorKey ? attrs[colorKey] : undefined;
		const variantSize = sizeKey ? attrs[sizeKey] : undefined;

		if (variantColor !== colorId || variantSize !== sizeId) continue;

		if (wantedBaseSize && baseSizeKey) {
			if (normalizeSizeValue(attrs[baseSizeKey]) !== wantedBaseSize) continue;
		}

		return v;
	}

	return null;
}

/** True when a wrapper_pricing service entry is a paid add-on (as opposed
 *  to a bundled/free service) — i.e. `addon: "yes"` in the API response. */
function isAddonService(svc: any): boolean {
	return String(svc?.addon ?? "").trim().toLowerCase() === "yes";
}

/**
 * Resolve a paid add-on service's real price straight from wrapper_pricing's
 * own `price_breakdown` — NEVER `markup_price_breakdown`, since fixed-price
 * mode wipes that to 0 for every service. Falls back across the shapes the
 * API has used (nested product_price.base_price, flat base_price, or
 * total_cost) and multiplies by the service's own order_quantity.
 */
function getAddonServicePrice(svc: any): number {
	const readUnit = (bd: any): number =>
		Number(
			bd?.product_price?.base_price ?? bd?.base_price ?? bd?.total_cost ?? 0,
		) || 0;

	let unit = readUnit(svc?.markup_price_breakdown);
	if (unit <= 0) unit = readUnit(svc?.price_breakdown);
	if (unit <= 0) unit = Number(svc?.base_price ?? 0) || 0;

	const qty = Math.max(1, Number(svc?.order_quantity ?? 1) || 1);
	return Math.round(unit * qty * 100) / 100;
}

/**
 * Show the existing "request price" / zero-price enquiry popup
 * (window.sdwpShowZeroPricePopup, defined elsewhere in the plugin's front-end
 * JS). Used both when the live wrapper_pricing API returns all-zero pricing,
 * and — in fixed-price mode — when no live warehouse data is available at
 * all. Retries once after 300ms in case the popup script hasn't finished
 * registering the global function yet.
 */
function showZeroPricePopup(productName: string, modelStateId: string): void {
	const productUrl: string = window.location.href;

	if (typeof (window as any).sdwpShowZeroPricePopup === "function") {
		(window as any).sdwpShowZeroPricePopup(productName, productUrl, modelStateId);
	} else {
		console.warn("sdwpShowZeroPricePopup not found — retrying in 300ms");
		setTimeout(() => {
			if (typeof (window as any).sdwpShowZeroPricePopup === "function") {
				(window as any).sdwpShowZeroPricePopup(productName, productUrl, modelStateId);
			} else {
				console.error("sdwpShowZeroPricePopup still unavailable — check plugin activation");
			}
		}, 300);
	}
}

function parsePromiseDateFromDisplayMessage(displayMessage: string): string | null {
	if (!displayMessage) return null;

	const MONTHS: Record<string, string> = {
		jan: "01", feb: "02", mar: "03", apr: "04", may: "05", jun: "06",
		jul: "07", aug: "08", sep: "09", oct: "10", nov: "11", dec: "12",
	};

	// Matches "Mar 07, 2027" / "March 7, 2027" etc.
	const match = displayMessage.match(/([A-Za-z]{3,})\s+(\d{1,2}),?\s+(\d{4})/);
	if (!match) return null;

	const monthKey = match[1].slice(0, 3).toLowerCase();
	const month = MONTHS[monthKey];
	if (!month) return null;

	const day = match[2].padStart(2, "0");
	const year = match[3];

	return `${year}-${month}-${day}`;
}

async function getFreshNonce(): Promise<string> {
	// 1. Use window-provided nonce if available and recent
	const windowNonce = (window as any).GC_MY_IMAGES?.nonce
		|| (window as any).shapediverAjax?.nonce
		|| (window as any).wpApiSettings?.nonce
		|| "";

	if (windowNonce) return windowNonce;

	// 2. Use cached nonce if still fresh
	const now = Date.now();
	if (cachedNonce && (now - nonceFetchedAt) < NONCE_TTL_MS) {
		return cachedNonce;
	}

	// 3. Fetch a fresh nonce from WP REST API
	try {
		const resp = await fetch("/wp-json/", {
			method: "GET",
			credentials: "include",
		});
		const data = await resp.json();
		const freshNonce = data?.nonce || "";
		if (freshNonce) {
			cachedNonce = freshNonce;
			nonceFetchedAt = now;
			return freshNonce;
		}
	} catch (e) {
		// silent
	}

	// 4. Fallback: fetch nonce via admin-ajax
	try {
		const resp = await fetch("/wp-admin/admin-ajax.php", {
			method: "POST",
			credentials: "include",
			headers: { "Content-Type": "application/x-www-form-urlencoded" },
			body: new URLSearchParams({ action: "shapediver_check_login_status" }),
		});
		const data = await resp.json();
		const freshNonce = data?.data?.nonce || "";
		if (freshNonce) {
			cachedNonce = freshNonce;
			nonceFetchedAt = now;
			return freshNonce;
		}
	} catch (e) {
		// silent
	}

	return cachedNonce || "";
}

async function fetchFromGraphicsApi(
	queryString: string,
	ignoreCache: boolean,
): Promise<IGraphicsApiResponse> {
	if (!ignoreCache && cachedResults[queryString]) {
		return cachedResults[queryString];
	}

	const url = buildGraphicsApiUrl(queryString);

	const nonce = await getFreshNonce();

	const resp = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: {
			Accept: "application/json",
			"X-WP-Nonce": nonce,
		},
	});

	if (!resp.ok) {
		const text = await resp.text().catch(() => "");
		throw new Error(
			`Failed to fetch from graphics api: ${resp.status} ${resp.statusText} ${text}`,
		);
	}

	const json = await resp.json().catch((err) => {
		throw new Error(
			`Failed to parse graphics api response: ${String(err)}`,
		);
	});

	if (!json || typeof json !== "object")
		throw new Error("Invalid graphics api response: not an object");

	const result = json as IGraphicsApiResponse;
	cachedResults[queryString] = result;
	return result;
}

// ============================================================================
// ARTWORK TILE LABEL (category name only in search / tag results)
// ============================================================================

/**
 * Zero-width label used when a tile should look "blank". An empty string
 * isn't used because AppBuilder falls back to showing the item id when
 * displayname is empty.
 */
const EMPTY_TILE_LABEL = "\u200B\u2063";

/**
 * Label shown under an artwork tile.
 *  - Browsing a category folder → blank (you're already inside that category)
 *  - Searching / filtering by tag → the artwork's category name, since
 *    results can come from many different categories
 */
function getArtworkTileLabel(p: IProduct, isSearchMode: boolean): string {
	if (!isSearchMode) return EMPTY_TILE_LABEL;

	const structure = (p.category_structure || []).filter(
		(s) => typeof s === "string" && s.trim() !== "",
	);

	const label =
		structure[1]?.trim() ||                       // 2nd-level category (previous behaviour)
		p.category_name?.trim() ||                    // API's own category name
		structure[structure.length - 1]?.trim() ||    // deepest category in the path
		"";

	return label || EMPTY_TILE_LABEL;
}

/** True when the query is a text search or a tag filter (not plain folder browsing). */
function isSearchQuery(queryString: string): boolean {
	const params    = new URLSearchParams(queryString);
	const hasSearch = params.has("search") && params.get("search") !== "";
	const hasTags   = params.has("tags")   && params.get("tags")   !== "all";
	return hasSearch || hasTags;
}

async function returnAndMapCachedResults(): Promise<IScrollingApiLoadMoreReply<unknown>> {
	const result: IScrollingApiLoadMoreReply<unknown> = {
		hasNextPage: false,
		items: [],
	};

	// Search mode = user typed a search term or picked a tag filter.
	// Browse mode = user is just clicking through category folders.
	const isSearchMode      = isSearchQuery(latestQuery);
	const shouldShowFolders = currentIndex === 0 && !isSearchMode;

	// ── Category folders (browse mode, first page only) ─────────────────
	if (shouldShowFolders) {
		const categoryData = await fetchFromGraphicsApi(allCategoriesQuery, false);
		let categories = categoryData.categories;

		if (categories) {
			for (let i = 0; i < latestCategorySlugs.length; i++) {
				const categorySlug = latestCategorySlugs[i];
				const matchedCategory: ICategory | undefined = categories.find(
					(cat) => cat.slug === categorySlug,
				);
				if (!matchedCategory)
					throw new Error(
						`Category with slug "${categorySlug}" not found`,
					);
				categories = matchedCategory.children;
			}

			const categoryItems: IScrollingApiItemTypeSelect[] = categories.map((c) => ({
				item: `search:${categoryIdentifier}` + c.name,
				data: {
					displayname: c.name,          // folder tiles always keep their name
					imageUrl: c.thumbnail_url,
					data: c,
				},
			}));
			result.items = categoryItems;
		}
	}

	// ── Artwork tiles ────────────────────────────────────────────────────
	// FIX: only advance currentIndex when there are actual products to page
	// through. `products` can be a *defined but empty* array for a
	// folder-only category (e.g. "Art", which only has sub-categories and
	// no artwork of its own) — `if (products)` was truthy for `[]` too, so
	// currentIndex kept incrementing on every render of that category even
	// though nothing was ever paginated. Once currentIndex drifted away
	// from 0, `shouldShowFolders` (which requires currentIndex === 0)
	// permanently stopped showing that category's folder tiles, so
	// reopening the Artwork accordion later rendered a blank list instead
	// of the folders it showed the first time.
	const products = cachedResults[latestQuery]?.products;
	if (products && products.length > 0) {
		const productItems: IScrollingApiItemTypeSelect[] = products
			.slice(currentIndex, currentIndex + pageSize)
			.map((p) => ({
				item: p.graphic_id + "",
				data: {
					displayname: getArtworkTileLabel(p, isSearchMode),
					tooltip: p.description,
					imageUrl:
						p.downloadable_files?.find((f) => f.sessionId?.includes("thumbnail"))
							?.parameterValues.file_url
						?? p.thumbnail_url
						?? p.image_url,
					data: p,
				},
			}));
		currentIndex += pageSize;

		result.hasNextPage = currentIndex < products.length;
		result.items = result.items.concat(productItems);
	}

	return result;
}

async function scrollingApiSetParameters(
	data: IScrollingApiSetParametersData,
): Promise<IScrollingApiSetParametersReply<unknown>> {
	if (data.source !== "graphics") {
		console.debug(`Unsupported data source name: ${data.source}`);
		clearCache();
		return {hasNextPage: false, items: []};
	}

	if (data.reset) {
		resetCacheCallback = data.reset;
	}

	// FIX: AppBuilder calls setParameters with NO `terms` whenever the
	// scrolling widget mounts fresh with no filter selected yet — this
	// happens on first load, and again every time the Artwork accordion
	// is collapsed and reopened (the widget is destroyed and recreated,
	// so its own breadcrumb/navigation history is gone).
	//
	// latestQuery / latestCategorySlugs / currentIndex are module-level
	// state that survives that remount untouched. Previously, only the
	// `if (data.terms)` branch below ever updated them — so a "no terms"
	// remount call fell straight through to returnAndMapCachedResults()
	// with whatever sub-category (e.g. Art > Flat Carve) was last active.
	// AppBuilder's own breadcrumb was reset to nothing, but our returned
	// items were still 2 folders deep — the mismatch that left the
	// Artwork panel stuck mid-tree with no way back to "Art".
	//
	// Bring our own navigation state back to the Art root on every such
	// "fresh" call, so a re-opened Artwork panel always starts back at
	// the top instead of stranding the user deep in a sub-category.
	if (!data.terms || data.terms.length === 0) {
		latestQuery = allCategoriesQuery;
		latestCategorySlugs = [];
		currentIndex = 0;
	}

	if (data.terms) {
		const categories: string[] = [];
		const categoryIds: number[] = [];
		let tags: string = "";
		let search: string = "";
		const categoryData = await fetchFromGraphicsApi(
			allCategoriesQuery,
			false,
		);
		let cachedCategories = categoryData.categories;
		data.terms?.forEach((v: string) => {
			if (v.startsWith(categoryIdentifier)) {
				if (cachedCategories) {
					const categoryName = v.substring(categoryIdentifier.length);
					const matchedCategory = cachedCategories.find(
						(cat) => cat.name === categoryName,
					);
					if (matchedCategory) {
						categories.push(matchedCategory.slug);
						categoryIds.push(matchedCategory.id);
						cachedCategories = matchedCategory.children;
					}
				}
			} else if (v.startsWith("tag:")) tags = v.substring("tag:".length);
			else search = v;
		});
		const queryString = getGraphicsApiQueryString(
			categories,
			tags,
			search,
			categoryIds,
		);
		if (queryString !== latestQuery) {
			latestQuery = queryString;
			latestCategorySlugs = categories;
			currentIndex = 0;
		}
	}

	if (data.pageSize !== undefined) {
		pageSize = data.pageSize;
		currentIndex = 0;
	}

	await fetchFromGraphicsApi(latestQuery, false);
	return returnAndMapCachedResults();
}

async function scrollingApiLoadMore(
	data: IScrollingApiLoadMoreData,
): Promise<IScrollingApiLoadMoreReply<unknown>> {
	if (data.source !== "graphics") {
		console.debug(`Unsupported data source name: ${data.source}`);
		clearCache();
		return {hasNextPage: false, items: []};
	}

	await fetchFromGraphicsApi(latestQuery, false);
	return returnAndMapCachedResults();
}

// ============================================================================
// DEFAULT — SpecificECommerceApiActions CLASS
// ============================================================================

class SpecificECommerceApiActions implements IECommerceApiActions {
	private productDataManager: ProductDataManager;
	private graphicsApiManager: GraphicsApiManager;

	constructor(private defaultActions?: IECommerceApiActions) {
		this.productDataManager = ProductDataManager.getInstance();
		this.graphicsApiManager = GraphicsApiManager.getInstance();
	}

	getParentPageInfo(): Promise<IGetParentPageInfoReply> {
		if (this.defaultActions) return this.defaultActions.getParentPageInfo();
		return Promise.resolve({href: window.location.href});
	}

	closeConfigurator(): Promise<boolean> {
		if (this.defaultActions) return this.defaultActions.closeConfigurator();

		return Promise.resolve(false);
	}

	addItemToCart(data: IAddItemToCartData): Promise<IAddItemToCartReply> {
		const {description, modelStateId, imageUrl} = data;

		console.log("🛒 ADD ITEM TO CART | Model State ID:", modelStateId);

		if (imageUrl) (window as any).shapeDiverImageUrl = imageUrl;

		let pricingParameters: Record<string, any> = {};
		if (description) {
			try {
				pricingParameters = JSON.parse(description);
			} catch (e) {
				console.warn(`Failed to parse pricing parameters from description: ${description}`, e);
			}
		}

		const doAddToCart = async (): Promise<IAddItemToCartReply> => {

			// ── Resolve product data (with lazy-load fallback) ─────────────
			let productData = this.productDataManager.getProductData();

			if (!productData) {
				console.warn("⚠️ Product data not in cache — attempting lazy load...");

				const btn = (
					document.getElementById("custom-sd-open-btn") ||
					document.getElementById("app-builder-open-configurator")
				) as HTMLElement | null;
				const productIdAttr = btn?.getAttribute("data-product-id");

				if (productIdAttr) {
					productData = await this.productDataManager.loadProductData(productIdAttr);
				}

				if (!productData) {
					productData = this.productDataManager.getProductData();
				}
			}

			if (!productData) {
				console.error("❌ No product data available after lazy load!");
				alert("Product information missing. Please close the configurator, refresh the page and try again.");
				return {id: modelStateId || "ERROR"};
			}

			// ── Debug: log all variants + attributes for this product ─────────
			logProductVariantsToConsole(productData.id.toString());

			const domVariationIdRaw = document
				.querySelector<HTMLInputElement>('input[name="variation_id"]')
				?.value;
			const domVariationId = domVariationIdRaw ? parseInt(domVariationIdRaw, 10) : 0;

			if (domVariationId > 0 && domVariationId !== productData.variation_id) {
				console.log("🔗 Resolved WooCommerce variation from DOM:", domVariationId, "— refreshing product data");
				const refreshedData = await this.productDataManager.loadProductData(productData.id, domVariationId);
				if (refreshedData) {
					productData = refreshedData;
				} else {
					console.warn("⚠️ Failed to refresh product data for variation", domVariationId, "— continuing with parent-level data");
				}
			}

			pricingParameters.product_id     = productData.id;
			pricingParameters.variation_id   = productData.variation_id   || 0;
			pricingParameters.variation_name = productData.variation_name || "";
			pricingParameters.model_state_id = modelStateId || productData.model_state_id;

			pricingParameters.sd_configurator_url =
				developmentUrlBuilderOptions.baseUrl || "";
			pricingParameters.sd_slug =
				productData.slug || "";
			pricingParameters.sd_settings_url =
				productData.settings_url || "";
			pricingParameters.sd_parameters_settings_url =
				(productData as any).query_params?._parameters_settings_url || "";

			if (!pricingParameters.product_id) {
				console.error("❌ CRITICAL: No product_id in pricing parameters!");
				alert("Product information missing. Please refresh the page and try again.");
				return {id: modelStateId || "ERROR"};
			}

			const validModelStateId = modelStateId || "TEMP_STATE_ID";

			// ── Family name logic ───────────────────────────────────────────
			let familyName = ((window as any).shapeDiverFamilyName as string | undefined)?.trim();

			if (!familyName && modelStateId) {
				const cartTbplUid = (window as any).shapeDiverTbplUid || "";
				familyName = await this.fetchExistingDesignTitle(modelStateId, cartTbplUid);
				if (familyName) (window as any).shapeDiverFamilyName = familyName;
			}

			if (!familyName) {
				console.log("📝 No family name — prompting before add to cart...");
				const entered = await this.promptFamilyNameModal();
				if (!entered) {
					console.warn("⚠️ User cancelled family name — add to cart aborted");
					return {id: validModelStateId};
				}
				familyName = entered;
				(window as any).shapeDiverFamilyName = familyName;

				const url = new URL(window.location.href);
				if (modelStateId) url.searchParams.set("modelStateId", modelStateId);
				const href = url.toString();
				const savedImageUrl = (window as any).shapeDiverImageUrl || "";
				const originalModelStateId = new URL(window.location.href).searchParams.get("modelStateId") || null;

				try {
					await this.saveDesign(
						modelStateId || validModelStateId,
						href,
						familyName,
						savedImageUrl,
						originalModelStateId,
						true,
					);
					console.log("✅ Family name saved silently before cart");
				} catch (err) {
					console.warn("⚠️ Silent save failed (continuing to cart anyway):", err);
				}
			}

			pricingParameters.design_family_name = familyName;


			// ── FIXED PRICE MODE ────────────────────────────────────────────────────
			// When enable_fixed_price is "1" in plugin settings, don't call the
			// wrapper_pricing API at all for PRICING. Instead:
			//   1. Call wrapper_pricing ONCE purely to harvest per-item metadata
			//      (name, sku, color_id, size_id, size, finish, services, ...) for
			//      EVERY physical item the API returns for this product (e.g. a
			//      multi-piece product like Top + Base).
			//   2. Match the first item's color_id + size_id — AND the Base item's
			//      `size` (e.g. "34x12x6") against pa_base-sizes — against the real
			//      WooCommerce variation list (get_product_variants_debug) to find
			//      the exact variation.
			//   3. Take THAT variation's own Regular Price and per-variation "Global
			//      Setting Cost" (attribute_pa_global-setting-cost).
			//   4. Apply sales tax on top of the regular price to get the grand total.
			// If no variant matches, fall back to the LIVE wrapper_pricing warehouse
			// data (see the `hasUnmatchedItem || totalRegularPrice === 0` branch
			// below). The "request price" popup is only the last resort, when
			// wrapper_pricing itself returns no warehouse data at all.
			const isFixedPriceMode: boolean =
				String((window as any).configuratorData?.settings?.enable_fixed_price ?? "0") === "1";

			if (isFixedPriceMode) {

				// ── Step 1: fetch per-item metadata (incl. color_id/size_id/size) from
				// the real pricing API. Price fields from this response are NEVER used
				// — only metadata + the join keys.
				const METADATA_KEYS = [
					"sku", "size",
					"color", "color_id", "color_name", "color_slug",
					"finish", "finish_id", "finish_name", "finish_slug",
					"category_id", "sub_category_id",
					"addon",
				] as const;

				let apiMetaItems: any[] = [];
				let apiMetaAvailableQuantity: number = 0;
				let apiMetaAvailabilityStatus: string = "In Stock";
				let apiMetaDisplayMessage: string = "";
				let apiMetaWarehouseName: string = "";
				let apiMetaWarehouseCode: string = "";
				let apiMetaRawWarehouse: any = null;
				let liveApiResponse: any = null;   // keep the full response, not just warehouse[0]

				const metaLoadingOverlay = document.getElementById("warehouse-loading-overlay");
				if (metaLoadingOverlay) metaLoadingOverlay.style.display = "flex";

				try {
					const metaResponse = await this.fetchPricingWithRetry(pricingParameters);
					liveApiResponse = metaResponse;
					const metaWarehouse = metaResponse?.warehouses?.[0];
					apiMetaRawWarehouse = metaWarehouse ? JSON.parse(JSON.stringify(metaWarehouse)) : null;
					apiMetaItems = metaWarehouse?.items ?? [];
					apiMetaAvailableQuantity = metaWarehouse?.available_quantity ?? 0;
					apiMetaAvailabilityStatus = metaWarehouse?.availability_status ?? "In Stock";
					apiMetaDisplayMessage = metaWarehouse?.display_message ?? "";
					apiMetaWarehouseName = metaWarehouse?.warehouse_name ?? "";
					apiMetaWarehouseCode = metaWarehouse?.warehouse_code ?? "";
				} catch (err) {
					console.warn(
						"⚠️ Fixed price mode: could not fetch item metadata from wrapper_pricing (continuing with blank metadata):",
						err,
					);
				} finally {
					if (metaLoadingOverlay) metaLoadingOverlay.style.display = "none";
				}

				// ── Step 2: fetch the real WooCommerce variation list for this product.
				const variants = await fetchProductVariants(productData.id);

				const pickMetadata = (apiItem: any): Record<string, any> => {
					const fields: Record<string, any> = {};
					for (const key of METADATA_KEYS) {
						if (apiItem?.[key] !== undefined) fields[key] = apiItem[key];
					}
					return fields;
				};

				let totalRegularPrice = 0;
				let totalSetupCost = 0;
				let fixedItems: WarehouseLineItem[];
				// Set to true the moment the item fails to match a real WooCommerce
				// variation. When true, we fall back to live wrapper_pricing warehouse
				// data (see below) instead of the "request price" popup.
				let hasUnmatchedItem = false;

				if (apiMetaItems.length > 0) {
					// Matching/pricing is based on the first item's color_id/size_id,
					// plus the Base item's size (matched to pa_base-sizes).
					const firstApiItem = apiMetaItems[0];

					// Size of the "Base" piece (e.g. "34x12x6"), matched against pa_base-sizes
					const baseItemSize = findBaseItemSize(apiMetaItems);

					const matchedVariant = matchVariantByColorSize(
						variants,
						firstApiItem.color_id,
						firstApiItem.size_id,
						baseItemSize,
					);

					let itemRegularPrice = 0;
					let itemSetupCost = 0;

					if (matchedVariant) {
						itemRegularPrice = parseFloat(matchedVariant.regular_price || "0") || 0;
						itemSetupCost = parseFloat(
							matchedVariant.setup_cost !== undefined
								? matchedVariant.setup_cost
								: "0",
						) || 0;

						console.log(
							`✅ Matched variant for "${firstApiItem.name}" — color_id:${firstApiItem.color_id} size_id:${firstApiItem.size_id}` +
							` base_size:${baseItemSize ?? "(none)"}` +
							` → variation ${matchedVariant.variation_id} (sku:${matchedVariant.sku}),` +
							` regular_price:${itemRegularPrice}, setup_cost:${itemSetupCost}`,
						);
					} else {
						hasUnmatchedItem = true;
						console.warn(
							`⚠️ No WooCommerce variant matched for item "${firstApiItem.name}"` +
							` (color_id:${firstApiItem.color_id}, size_id:${firstApiItem.size_id},` +
							` base_size:${baseItemSize ?? "(none)"}).` +
							` No reliable fixed price available — will fall back to live wrapper_pricing` +
							` warehouse data for the warehouse popup. Check that the product's variation` +
							` attributes (pa_global_color / pa_global_size / pa_base-sizes) match the` +
							` values returned by wrapper_pricing.`,
						);
					}

					// Only the FIRST item contributes to the price total.
					totalRegularPrice += itemRegularPrice;
					totalSetupCost += itemSetupCost;

					// ── Display: build a line item for EVERY item wrapper_pricing returned,
					// not just the first. These are shown in the cart/warehouse popup, but
					// only the first item's price (computed above) counts toward the total.
					let totalAddonPrice = 0;

					fixedItems = apiMetaItems.map((apiItem, idx) => {
						const isPricedItem = idx === 0;

						const mappedServices = Array.isArray(apiItem?.services)
							? apiItem.services.map((svc: any) => {
								const addon = isAddonService(svc);
								const svcPrice = addon ? getAddonServicePrice(svc) : 0;
								if (addon) totalAddonPrice += svcPrice;

								return {
									...pickMetadata(svc),
									name: svc?.name || "Service",
									order_quantity: svc?.order_quantity ?? 1,
									rate: svcPrice,
									unit_price: svcPrice,
									markup_price_breakdown: {
										base_price: svcPrice,
										rate: svcPrice,
										unit_price: svcPrice,
										total_cost: svcPrice,
									},
								};
							})
							: [];

						return {
							...pickMetadata(apiItem),
							name: apiItem.name || (isPricedItem ? productData!.name : undefined) || "Product",
							order_quantity: 1,
							rate: isPricedItem ? itemRegularPrice : 0,
							unit_price: isPricedItem ? itemRegularPrice : 0,
							markup_price_breakdown: {
								base_price: isPricedItem ? itemRegularPrice : 0,
								rate: isPricedItem ? itemRegularPrice : 0,
								unit_price: isPricedItem ? itemRegularPrice : 0,
								total_cost: isPricedItem ? itemRegularPrice : 0,
							},
							services: mappedServices,
						};
					});

					// Fold paid add-on services into the priced total so sales tax
					// and the grand total are computed on the real, full amount —
					// not just the physical item's own price.
					totalRegularPrice += totalAddonPrice;

					if (totalAddonPrice > 0) {
						console.log(`💰 Fixed price mode — add-on services total: $${totalAddonPrice.toFixed(2)}`);
					}

					if (apiMetaItems.length > 1) {
						console.log(
							`ℹ️ wrapper_pricing returned ${apiMetaItems.length} items — ` +
							`only the first ("${firstApiItem.name}") is used for fixed-price pricing ` +
							`(base size "${baseItemSize ?? "(none)"}" used for variant matching); ` +
							`the rest are shown in the cart display with $0 price contribution.`,
						);
					}
				} else {
					// Metadata fetch failed/empty — fall back to the product-level
					// price/setup-cost already resolved onto productData (single blank
					// metadata row), same as the pre-multi-item fallback behaviour.
					totalRegularPrice = parseFloat(productData.regular_price || productData.price || "0") || 0;
					totalSetupCost = parseFloat(productData.setup_cost || "0") || 0;

					fixedItems = [{
						name: productData.name || "Product",
						sku: (productData as any).sku || "",
						order_quantity: 1,
						rate: totalRegularPrice,
						unit_price: totalRegularPrice,
						markup_price_breakdown: {
							base_price: totalRegularPrice,
							rate: totalRegularPrice,
							unit_price: totalRegularPrice,
							total_cost: totalRegularPrice,
						},
						services: [], // no metadata to draw real services from here — Setup Cost surfaces via pricing_summary.costs.setup_cost, not as a per-item service
					}];
				}

				// Sales tax applies to the product cost (+ setup cost is added
				// separately below, matching the live wrapper_pricing API's
				// `sales_tax` cost base).
				const fixedPriceSalesTaxRate = getFixedPriceSalesTaxRate();
				const salesTax = Math.round(
					totalRegularPrice * fixedPriceSalesTaxRate * 100
				) / 100;

				const grandTotal = totalRegularPrice + salesTax + totalSetupCost;

				const today = new Date();
				const pad = (n: number) => String(n).padStart(2, "0");
				const placeholderPromiseDate = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
				// Prefer the real promise date parsed from wrapper_pricing's display_message
				// (e.g. "Expected delivery on Mar 07, 2027.") — fall back to today's date
				// only if it couldn't be parsed (e.g. blank/unexpected message format).
				const resolvedPromiseDate =
					parsePromiseDateFromDisplayMessage(apiMetaDisplayMessage) || placeholderPromiseDate;

				// Build costs generically so it renders the same way live-pricing costs do.
				const fixedCosts: Record<string, number> = {sales_tax: salesTax};
				if (totalSetupCost > 0) fixedCosts.setup_cost = totalSetupCost;

				const fixedWarehouse: WarehouseData = {
					warehouse_code: apiMetaWarehouseCode || "FIXED",
					warehouse_name: apiMetaWarehouseName || "Standard Pricing",
					availability_status: apiMetaAvailabilityStatus,
					available_quantity: apiMetaAvailableQuantity,
					promise_date: resolvedPromiseDate,
					display_message: apiMetaDisplayMessage,
					items: fixedItems,
					pricing_summary: {
						base_price: totalRegularPrice,
						product_price: {base_price: totalRegularPrice},
						costs: fixedCosts,
						discounts: {},
						total_cost: grandTotal,
						is_fixed_price: true,
					},
					markup_pricing_summary: {
						base_price: totalRegularPrice,
						product_price: {base_price: totalRegularPrice},
						costs: fixedCosts,
						discounts: {},
						total_cost: grandTotal,
						is_fixed_price: true,
					},
				};

				console.log(
					"💲 Fixed price mode — Total regular price:", totalRegularPrice,
					"| Total setup cost:", totalSetupCost,
					`| Sales tax (${(fixedPriceSalesTaxRate * 100).toFixed(2)}%):`, salesTax,
					"| Grand total:", grandTotal,
					"| Items from API:", apiMetaItems.length,
					"| Variants available:", variants.length,
					"| Unmatched item(s):", hasUnmatchedItem,
				);

				// ── No reliable fixed price — fall back to the live wrapper_pricing
				// warehouse data so the customer still gets a warehouse popup with real
				// availability/date info, rather than being dropped straight into the
				// "request price" enquiry form.
				if (hasUnmatchedItem || totalRegularPrice === 0) {
					console.log(
						"💰 Fixed price mode — unmatched item(s) or zero total — using live product_price + fixed-mode sales tax & max setting cost",
					);

					if (liveApiResponse?.warehouses?.length > 0) {
						// Byte-exact raw snapshot per warehouse (liveApiResponse itself is never mutated)
						const rawWarehousesByCode: Record<string, any> = {};
						liveApiResponse.warehouses.forEach((w: any) => {
							rawWarehousesByCode[w.warehouse_code] = JSON.parse(JSON.stringify(w));
						});

						// Highest per-variation setting cost for this product
						const maxSetupCost: number = variants.reduce((max: number, v: any) => {
							const cost = parseFloat(v?.setup_cost ?? "0");
							return Number.isFinite(cost) && cost > max ? cost : max;
						}, 0);

						const salesTaxRate = getFixedPriceSalesTaxRate();

						const hybridWarehouses: WarehouseData[] = liveApiResponse.warehouses.map((w: any) => {
							const livePricing = w.markup_pricing_summary || w.pricing_summary || {};
							const productPrice =
								Number(livePricing.product_price?.base_price ?? livePricing.base_price ?? 0) || 0;

							const hybridSalesTax = Math.round(productPrice * salesTaxRate * 100) / 100;

							const costs: Record<string, number> = {sales_tax: hybridSalesTax};
							if (maxSetupCost > 0) costs.setup_cost = maxSetupCost;

							const buildSummary = (): PricingSummary => ({
								base_price: productPrice,
								product_price: {base_price: productPrice},
								costs: {...costs},
								discounts: {},
								total_cost: productPrice + hybridSalesTax + maxSetupCost,
								is_fixed_price: true, // → "Other Costs (Sales Tax, Setting Cost, etc.)" label
							});

							return {
								...w,
								pricing_summary: buildSummary(),
								markup_pricing_summary: buildSummary(),
							};
						});

						console.log(
							"🔀 Hybrid pricing — max setting cost across variants:", maxSetupCost,
							`| Sales tax rate: ${(salesTaxRate * 100).toFixed(2)}%`,
							"| Warehouses:", hybridWarehouses.map((w) => ({
								code: w.warehouse_code,
								product_price: w.pricing_summary.product_price?.base_price,
								costs: w.pricing_summary.costs,
								total: w.pricing_summary.total_cost,
							})),
						);

						WarehouseManager.openPopup(
							hybridWarehouses,
							pricingParameters,
							validModelStateId,
							productData,
							liveApiResponse,      // untouched original response
							rawWarehousesByCode,
						);
						return {id: validModelStateId};
					}

					// No live warehouse data at all — this is the ONLY remaining path
					// to the "request price" enquiry popup.
					console.log("⚠️ No live warehouse data available from wrapper_pricing — showing request-price popup");
					const productName: string = productData?.name || document.title || "";
					showZeroPricePopup(productName, validModelStateId);
					return {id: validModelStateId};
				}

				WarehouseManager.openPopup(
					[fixedWarehouse],
					pricingParameters,
					validModelStateId,
					productData,
					null,
					{[fixedWarehouse.warehouse_code]: apiMetaRawWarehouse},
				);
				return {id: validModelStateId};
			}


			const loadingOverlay = document.getElementById("warehouse-loading-overlay");
			if (loadingOverlay) loadingOverlay.style.display = "flex";

			return this.fetchPricingWithRetry(pricingParameters)
				.then((apiResponse) => {
					if (loadingOverlay) loadingOverlay.style.display = "none";

					if (apiResponse && apiResponse.warehouses && apiResponse.warehouses.length > 0) {

						// Byte-exact snapshot of what wrapper_pricing actually
						// returned, taken BEFORE the client-side setup_cost
						// injection below. This is what gets sent to the server
						// as selected_warehouse_data_api_raw.
						const rawWarehousesByCode: Record<string, any> = {};
						apiResponse.warehouses.forEach((w: any) => {
							rawWarehousesByCode[w.warehouse_code] = JSON.parse(JSON.stringify(w));
						});

						const setupCost = parseFloat((productData as any)?.setup_cost || "0") || 0;
						if (setupCost > 0) {
							apiResponse.warehouses.forEach((w: any) => {
								[w.pricing_summary, w.markup_pricing_summary].forEach((summary: any) => {
									if (!summary) return;
									summary.costs = {...(summary.costs || {}), setup_cost: setupCost};
									summary.total_cost = (Number(summary.total_cost) || 0) + setupCost;
								});
							});
						}

						const allZero: boolean = apiResponse.warehouses.every((w: any) => {
							const pricing = w.markup_pricing_summary || w.pricing_summary || {};
							// Support both the new nested shape (product_price.base_price)
							// and the legacy flat shape (base_price).
							const total = Number(
								pricing.total_cost ?? pricing.product_price?.base_price ?? pricing.base_price,
							);
							return !total || total === 0;
						});

						if (allZero) {
							console.log("💰 All warehouses return zero cost — showing price-enquiry popup");

							const productName: string =
								productData?.name ||
								document.title   ||
								"";

							showZeroPricePopup(productName, validModelStateId);

							return {id: validModelStateId};
						}

						WarehouseManager.openPopup(
							apiResponse.warehouses,
							pricingParameters,
							validModelStateId,
							productData,
							apiResponse,
							rawWarehousesByCode,
						);
						return {id: validModelStateId};

					} else {
						throw new Error("No warehouse data available");
					}
				})
				.catch((error: any) => {
					if (loadingOverlay) loadingOverlay.style.display = "none";
					const errorMessage = error.message || "Unable to load pricing information. Please try again later.";
					console.error("❌ FINAL ERROR:", errorMessage);
					alert(`Unable to load pricing information.\n\nError: ${errorMessage}\n\nPlease try again or contact support if the issue persists.`);
					return {id: validModelStateId};
				});
		};

		return doAddToCart();
	}

	getUserProfile(): Promise<IGetUserProfileReply> {
		if (this.defaultActions) return this.defaultActions.getUserProfile();

		const reply: IGetUserProfileReply = {
			id: "DUMMY_ID",
			email: "john@doe.com",
			name: "John Doe",
		};

		return Promise.resolve(reply);
	}

	updateSharingLink(
		data: IUpdateSharingLinkData,
	): Promise<IUpdateSharingLinkReply> {
		const {modelStateId, imageUrl} = data;

		const url = new URL(window.location.href);
		const originalModelStateId = url.searchParams.get(QUERYPARAM_MODELSTATEID);

		const tbplUid = url.searchParams.get("_tbpl_uid") || "";
		if (tbplUid) (window as any).shapeDiverTbplUid = tbplUid;

		url.searchParams.set(QUERYPARAM_MODELSTATEID, modelStateId);
		const href = url.toString();
		history.replaceState(history.state, "", href);

		console.log("💾 Silent save triggered | New ID:", modelStateId, "| tbpl_uid:", tbplUid || "(none — new design)");

		// ── Pre-open blank tab HERE — synchronously, before any await ─────────
		// updateSharingLink() is called directly by the SDK (not inside an async
		// chain yet), so this window.open() is still within the user-gesture tick.
		// We open a blank tab immediately, then navigate it if login is needed,
		// or close it silently if the user is already logged in.
		const preOpenedTab: Window | null = window.open('', '_blank');

		const doSave = async (): Promise<IUpdateSharingLinkReply> => {

			const isLoggedIn: boolean = (window as any).shapediverAjax?.isLoggedIn === true
				|| configuratorData?.isLoggedIn === true;

			if (!isLoggedIn) {
				console.log("🔒 User not logged in — opening login in new tab");

				const currentUrl = new URL(window.location.href);
				currentUrl.searchParams.set(QUERYPARAM_MODELSTATEID, modelStateId);
				const urlWithState = currentUrl.toString();

				history.replaceState(history.state, "", urlWithState);

				// ── Store pending save data ────────────────────────────────────
				try {
					sessionStorage.setItem('shapediver_pending_save', JSON.stringify({
						modelStateId,
						imageUrl: imageUrl || (window as any).shapeDiverImageUrl || "",
						href: urlWithState,
					}));
				} catch (e) {}

				// ── Build login URL ────────────────────────────────────────────
				// redirect_to points to a lightweight success page that:
				//   1. Posts a BroadcastChannel message to the parent tab
				//   2. Closes itself
				const myAccountBase: string = (
					(window as any).configuratorData?.loginUrl
					|| `${window.location.origin}/my-account/`
				).split('?')[0];

				const successUrl = `${window.location.origin}/config-login-success/`;
				const loginUrl   = `${myAccountBase}?redirect_to=${encodeURIComponent(successUrl)}`;

				// ── Navigate the pre-opened blank tab ─────────────────────────
				if (preOpenedTab && !preOpenedTab.closed) {
					preOpenedTab.location.href = loginUrl;
				} else {
					// Pre-open was blocked (very rare) — fall back to same-tab redirect
					console.warn("⚠️ Pre-opened tab unavailable — falling back to same-tab redirect");
					try {
						sessionStorage.setItem('shapediver_login_redirect', urlWithState);
					} catch (e) {}
					window.location.href = loginUrl;
					return { href: urlWithState };
				}

				const loginTab = preOpenedTab;

				// ── Listen for login-complete via BroadcastChannel ────────────
				return new Promise<IUpdateSharingLinkReply>((resolve) => {
					const channel = new BroadcastChannel('shapediver_login');
					const MAX_WAIT = 10 * 60 * 1000; // 10 minutes
					let settled = false;

					const finish = async (loggedIn: boolean) => {
						if (settled) return;
						settled = true;
						channel.close();
						clearTimeout(timeoutId);

						if (!loggedIn) {
							console.warn("⚠️ Login tab closed without login — save aborted");
							resolve({ href: urlWithState });
							return;
						}

						console.log("✅ Login detected via BroadcastChannel — proceeding with save");

						// Update in-memory login flags
						if ((window as any).shapediverAjax)   (window as any).shapediverAjax.isLoggedIn   = true;
						if ((window as any).configuratorData) (window as any).configuratorData.isLoggedIn = true;

						// Close the login tab
						try { if (loginTab && !loginTab.closed) loginTab.close(); } catch (e) {}

						// Clear pending save marker
						try { sessionStorage.removeItem('shapediver_pending_save'); } catch (e) {}

						// Prefill family name if available
						let prefillTitle = "";
						if (modelStateId) prefillTitle = await this.fetchExistingDesignTitle(modelStateId, tbplUid);
						if (!prefillTitle) prefillTitle = ((window as any).shapeDiverFamilyName as string | undefined)?.trim() || "";
						if (!prefillTitle) prefillTitle = cachedLatestFamilyName || "";
						if (prefillTitle) (window as any).shapeDiverFamilyName = prefillTitle;

						// Prompt family name modal
						const familyName = await this.promptFamilyNameModal();
						if (!familyName) {
							console.warn("⚠️ User cancelled family name — save skipped");
							resolve({ href: urlWithState });
							return;
						}

						(window as any).shapeDiverFamilyName = familyName;

						await this.saveDesign(
							modelStateId,
							urlWithState,
							familyName,
							imageUrl || (window as any).shapeDiverImageUrl,
							null,
						);

						resolve({ href: urlWithState });
					};

					// BroadcastChannel message from config-login-success page
					channel.onmessage = (event) => {
						if (event.data?.type === 'shapediver_login_success') {
							finish(true);
						}
					};

					// Fallback: poll login status in case BroadcastChannel misses
					const POLL_INTERVAL = 2000;
					const pollLogin = setInterval(async () => {
						if (settled) { clearInterval(pollLogin); return; }
						try {
							const resp = await fetch(
								(window as any).configuratorData?.ajaxurl || '/wp-admin/admin-ajax.php',
								{
									method: 'POST',
									credentials: 'same-origin',
									headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
									body: new URLSearchParams({ action: 'shapediver_check_login_status' }),
								}
							);
							const result = await resp.json();
							if (result.success && result.data?.isLoggedIn) {
								clearInterval(pollLogin);
								finish(true);
							}
						} catch (e) { /* ignore */ }
					}, POLL_INTERVAL);

					// Tab-closed detector
					const tabWatcher = setInterval(() => {
						if (settled) { clearInterval(tabWatcher); return; }
						if (loginTab && loginTab.closed) {
							clearInterval(tabWatcher);
							clearInterval(pollLogin);
							// Give poll one last chance before declaring failure
							setTimeout(async () => {
								if (settled) return;
								try {
									const resp = await fetch(
										(window as any).configuratorData?.ajaxurl || '/wp-admin/admin-ajax.php',
										{
											method: 'POST',
											credentials: 'same-origin',
											headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
											body: new URLSearchParams({ action: 'shapediver_check_login_status' }),
										}
									);
									const result = await resp.json();
									finish(result.success && result.data?.isLoggedIn);
								} catch (e) {
									finish(false);
								}
							}, 500);
						}
					}, 1000);

					// Overall timeout
					const timeoutId = setTimeout(() => {
						clearInterval(pollLogin);
						clearInterval(tabWatcher);
						try { if (loginTab && !loginTab.closed) loginTab.close(); } catch (e) {}
						finish(false);
					}, MAX_WAIT);
				});
			}

			// Already logged in — close the pre-opened blank tab we don't need
			try { if (preOpenedTab && !preOpenedTab.closed) preOpenedTab.close(); } catch (e) {}

			if (imageUrl) {
				(window as any).shapeDiverImageUrl = imageUrl;
				console.log("🖼️ Uploading image to server...");
				try {
					const uploadedUrl = await this.uploadBase64Image(imageUrl, modelStateId);
					if (uploadedUrl) {
						(window as any).shapeDiverImageUrl = uploadedUrl;
						console.log("✅ Image uploaded, server URL stored:", uploadedUrl);
					}
				} catch (err) {
					console.warn("⚠️ Image upload failed, using base64 fallback:", err);
				}
			}

			let designTitle = ((window as any).shapeDiverFamilyName as string | undefined)?.trim();

			if (!designTitle && originalModelStateId) {
				designTitle = await this.fetchExistingDesignTitle(originalModelStateId, tbplUid);
				if (designTitle) (window as any).shapeDiverFamilyName = designTitle;
			}

			if (!designTitle) {
				console.log("📝 No family name found — prompting user via modal...");
				designTitle = await this.promptFamilyNameModal();
				if (designTitle) {
					(window as any).shapeDiverFamilyName = designTitle;
				} else {
					console.warn("⚠️ User cancelled family name input — save skipped");
					return {href};
				}
			}

			await this.saveDesign(
				modelStateId,
				href,
				designTitle,
				(window as any).shapeDiverImageUrl,
				originalModelStateId,
			);

			return {href};
		};

		return doSave();
	}

	async scrollingApiSetParameters(
		data: IScrollingApiSetParametersData,
	): Promise<IScrollingApiSetParametersReply<unknown>> {
		return scrollingApiSetParameters(data);
	}

	async scrollingApiLoadMore(
		data: IScrollingApiLoadMoreData,
	): Promise<IScrollingApiLoadMoreReply<unknown>> {
		return scrollingApiLoadMore(data);
	}

	async messageToParent(
		data: IMessageToParentData,
	): Promise<IMessageToParentReply> {
		if (data.type === "uploadImages") {

			const isLoggedIn: boolean = (window as any).shapediverAjax?.isLoggedIn === true
				|| (window as any).configuratorData?.isLoggedIn === true;

			if (!isLoggedIn) {
				console.log("🔒 Not logged in — opening login in new tab for image upload");

				// ── Open blank tab synchronously (messageToParent is called by the SDK
				// directly from a user gesture context, so window.open is allowed here) ──
				const uploadLoginTab: Window | null = window.open('', '_blank');

				try {
					sessionStorage.setItem('shapediver_pending_upload', JSON.stringify({
						intent: 'uploadImages',
						timestamp: Date.now(),
						returnUrl: window.location.href,
					}));
				} catch (e) {}

				const myAccountBase: string = (
					(window as any).configuratorData?.loginUrl
					|| `${window.location.origin}/my-account/`
				).split('?')[0];

				const successUrl = `${window.location.origin}/config-login-success/`;
				const loginUrl   = `${myAccountBase}?redirect_to=${encodeURIComponent(successUrl)}`;

				if (uploadLoginTab && !uploadLoginTab.closed) {
					uploadLoginTab.location.href = loginUrl;
				} else {
					// Fallback: same-tab redirect
					window.location.href = loginUrl;
					return { notification: { data: { message: "Redirecting to login..." } } };
				}

				// ── Wait for login via BroadcastChannel ───────────────────────
				return new Promise<IMessageToParentReply>((resolve) => {
					const channel    = new BroadcastChannel('shapediver_login');
					const MAX_WAIT   = 10 * 60 * 1000;
					let settled      = false;

					const finish = async (loggedIn: boolean) => {
						if (settled) return;
						settled = true;
						channel.close();
						clearTimeout(timeoutId);
						clearInterval(pollLogin);
						clearInterval(tabWatcher);

						try { if (uploadLoginTab && !uploadLoginTab.closed) uploadLoginTab.close(); } catch (e) {}
						try { sessionStorage.removeItem('shapediver_pending_upload'); } catch (e) {}

						if (!loggedIn) {
							resolve({ notification: { data: { message: "Login cancelled." } } });
							return;
						}

						console.log("✅ Login detected — proceeding with image upload");
						if ((window as any).shapediverAjax)   (window as any).shapediverAjax.isLoggedIn   = true;
						if ((window as any).configuratorData) (window as any).configuratorData.isLoggedIn = true;

						const success = await imageUpload();
						if (success) clearCache();
						resolve({
							notification: {
								data: {
									message: success ? "Image uploaded successfully." : "Image upload cancelled or failed.",
								},
							},
						});
					};

					channel.onmessage = (event) => {
						if (event.data?.type === 'shapediver_login_success') finish(true);
					};

					const pollLogin = setInterval(async () => {
						if (settled) { clearInterval(pollLogin); return; }
						try {
							const resp = await fetch(
								(window as any).configuratorData?.ajaxurl || '/wp-admin/admin-ajax.php',
								{
									method: 'POST',
									credentials: 'same-origin',
									headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
									body: new URLSearchParams({ action: 'shapediver_check_login_status' }),
								}
							);
							const result = await resp.json();
							if (result.success && result.data?.isLoggedIn) {
								clearInterval(pollLogin);
								finish(true);
							}
						} catch (e) { /* ignore */ }
					}, 2000);

					const tabWatcher = setInterval(() => {
						if (settled) { clearInterval(tabWatcher); return; }
						if (uploadLoginTab && uploadLoginTab.closed) {
							clearInterval(tabWatcher);
							clearInterval(pollLogin);
							setTimeout(async () => {
								if (settled) return;
								try {
									const resp = await fetch(
										(window as any).configuratorData?.ajaxurl || '/wp-admin/admin-ajax.php',
										{
											method: 'POST',
											credentials: 'same-origin',
											headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
											body: new URLSearchParams({ action: 'shapediver_check_login_status' }),
										}
									);
									const result = await resp.json();
									finish(result.success && result.data?.isLoggedIn);
								} catch (e) { finish(false); }
							}, 500);
						}
					}, 1000);

					const timeoutId = setTimeout(() => {
						try { if (uploadLoginTab && !uploadLoginTab.closed) uploadLoginTab.close(); } catch (e) {}
						finish(false);
					}, MAX_WAIT);
				});
			}

			// ── Already logged in — proceed directly ───────────────────────
			const success = await imageUpload();
			if (success) clearCache();
			return Promise.resolve({
				notification: {
					data: {
						message: success
							? "Image uploaded successfully."
							: "Image upload cancelled or failed.",
					},
				},
			});
		}
		return Promise.resolve({});
	}

	// --------------------------------------------------------------------------
	// Private helpers
	// --------------------------------------------------------------------------

	private async fetchPricingWithRetry(pricingParameters: Record<string, any>): Promise<any> {
		let lastError: any = null;

		console.log("🔧 pricingParameters:", JSON.stringify(pricingParameters, null, 2));

		for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
			try {
				console.log(`📤 Calling wrapper_pricing API (Attempt ${attempt + 1}/${MAX_RETRIES + 1})`);

				const apiUrl = `${window.location.origin}/wp-json/shapediver/v1/wrapper_pricing`;
				const requestBody = {type: "Pricing", data: pricingParameters};

				const controller = new AbortController();
				const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

				const response = await fetch(apiUrl, {
					method: "POST",
					headers: {"Content-Type": "application/json"},
					credentials: "same-origin",
					body: JSON.stringify(requestBody),
					signal: controller.signal,
				});

				clearTimeout(timeoutId);

				if (response.ok) {
					const result = await response.json();
					console.log("✅ Wrapper pricing response:", result);
					return result;
				} else {
					const errorText = await response.text();
					let errorData;
					try { errorData = JSON.parse(errorText); } catch (e) { errorData = {message: errorText}; }

					console.error("❌ API Error:", response.status, errorData);

					if (errorData.message && errorData.message.includes("Timeout")) {
						lastError = new Error("Connection timeout. The pricing server is not responding.");
						if (attempt < MAX_RETRIES) {
							console.log(`⏳ Retrying in ${RETRY_DELAY / 1000} seconds...`);
							await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
							continue;
						}
					} else {
						lastError = new Error(errorData.message || "Failed to load pricing information");
						break;
					}
				}
			} catch (error: any) {
				console.error("❌ Network error:", error);
				lastError = error.name === "AbortError"
					? new Error("Request timeout. Please try again.")
					: error;

				if (attempt < MAX_RETRIES) {
					console.log(`⏳ Retrying in ${RETRY_DELAY / 1000} seconds...`);
					await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
					continue;
				}
			}

			break;
		}

		throw lastError;
	}

	private async uploadBase64Image(base64ImageUrl: string, modelStateId: string): Promise<string | null> {
		try {
			const response = await fetch(base64ImageUrl);
			const blob = await response.blob();

			const fileName = `shapediver_${modelStateId}_${Date.now()}.png`;
			const file = new File([blob], fileName, {type: "image/png"});

			const formData = new FormData();
			formData.append("file", file);
			formData.append("model_state_id", modelStateId);
			formData.append("type", "configurator_snapshot");

			const productData = this.productDataManager.getProductData();
			if (productData) formData.append("product_id", productData.id.toString());

			const apiUrl = `${window.location.origin}/wp-json/shapediver/v1/upload-configurator-image`;

			const uploadResponse = await fetch(apiUrl, {
				method: "POST",
				body: formData,
				credentials: "same-origin",
			});

			if (!uploadResponse.ok) throw new Error(`HTTP ${uploadResponse.status}`);

			const result = await uploadResponse.json();
			return result.success && result.data?.url ? result.data.url : null;
		} catch (error) {
			console.error("❌ Image upload error:", error);
			return null;
		}
	}

	private async fetchExistingDesignTitle(modelStateId: string, tbplUid?: string): Promise<string> {
		try {
			const formData = new URLSearchParams();
			formData.append("action", "get_shapediver_design_by_state_id");
			formData.append("modelStateId", modelStateId);

			const uid = tbplUid || (window as any).shapeDiverTbplUid || "";
			if (uid) formData.append("tbpl_uid", uid);

			const nonce = (window as any).shapediverAjax?.nonce;
			if (nonce) formData.append("nonce", nonce);

			const response = await fetch("/wp-admin/admin-ajax.php", {
				method: "POST",
				credentials: "same-origin",
				headers: {"Content-Type": "application/x-www-form-urlencoded"},
				body: formData.toString(),
			});

			if (response.ok) {
				const result = await response.json();
				if (result.success && result.data?.designTitle) return result.data.designTitle;
			}
		} catch (error) {
			console.error("❌ Fetch existing title error:", error);
		}
		return "";
	}

	private showSaveModal(existingTitle: string = ""): Promise<string | null> {
		return new Promise((resolve) => {
			const modal = document.getElementById("shapediver-save-modal");
			const input = document.getElementById("design-title") as HTMLInputElement;

			if (!modal || !input) { resolve(null); return; }

			const errorDiv = modal.querySelector(".shapediver-error-message") as HTMLElement;
			const submitBtn = modal.querySelector(".shapediver-btn-submit") as HTMLButtonElement;
			const cancelBtn = modal.querySelector(".shapediver-btn-cancel") as HTMLButtonElement;
			const closeBtn = modal.querySelector(".shapediver-modal-close") as HTMLButtonElement;

			input.value = existingTitle.trim() !== "" ? existingTitle : "";
			if (errorDiv) errorDiv.style.display = "none";
			if (submitBtn) submitBtn.disabled = false;

			modal.style.display = "flex";
			setTimeout(() => { input.focus(); input.select(); }, 100);

			const hideModal = () => { modal.style.display = "none"; };

			const handleSubmit = () => {
				const title = input.value.trim();
				if (!title || title.length < 3) {
					if (errorDiv) {
						errorDiv.textContent = title ? "Title must be at least 3 characters" : "Please enter a design title";
						errorDiv.style.display = "block";
					}
					return;
				}
				hideModal(); cleanup(); resolve(title);
			};

			const handleCancel = () => { hideModal(); cleanup(); resolve(null); };

			const handleKeyPress = (e: KeyboardEvent) => {
				if (e.key === "Enter") { e.preventDefault(); handleSubmit(); }
				else if (e.key === "Escape") { handleCancel(); }
			};

			const handleBackdropClick = (e: MouseEvent) => { if (e.target === modal) handleCancel(); };

			const cleanup = () => {
				if (submitBtn) submitBtn.removeEventListener("click", handleSubmit);
				if (cancelBtn) cancelBtn.removeEventListener("click", handleCancel);
				if (closeBtn) closeBtn.removeEventListener("click", handleCancel);
				input.removeEventListener("keypress", handleKeyPress);
				modal.removeEventListener("click", handleBackdropClick);
			};

			if (submitBtn) submitBtn.addEventListener("click", handleSubmit);
			if (cancelBtn) cancelBtn.addEventListener("click", handleCancel);
			if (closeBtn) closeBtn.addEventListener("click", handleCancel);
			input.addEventListener("keypress", handleKeyPress);
			modal.addEventListener("click", handleBackdropClick);
		});
	}

	private promptFamilyNameModal(): Promise<string | undefined> {
		return new Promise((resolve) => {
			const modal    = document.getElementById("shapediver-save-modal");
			const input    = document.getElementById("design-title") as HTMLInputElement | null;
			const errorDiv = modal?.querySelector(".shapediver-error-message") as HTMLElement | null;
			const submitBtn = modal?.querySelector(".shapediver-btn-submit") as HTMLElement | null;
			const cancelBtn = modal?.querySelector(".shapediver-btn-cancel") as HTMLElement | null;
			const closeBtn  = modal?.querySelector(".shapediver-modal-close") as HTMLElement | null;

			if (!modal || !input) {
				const name = window.prompt("Enter a family name for your design:");
				resolve(name && name.trim().length >= 3 ? name.trim() : undefined);
				return;
			}

			const prefill = ((window as any).shapeDiverFamilyName as string | undefined)?.trim() || "";
			input.value = prefill;
			if (errorDiv) errorDiv.style.display = "none";

			modal.style.display = "flex";
			setTimeout(() => { input.focus(); input.select(); }, 100);

			const hideModal = () => { modal.style.display = "none"; };

			const cleanup = () => {
				submitBtn?.removeEventListener("click", handleSubmit);
				cancelBtn?.removeEventListener("click", handleCancel);
				closeBtn?.removeEventListener("click", handleCancel);
				input.removeEventListener("keydown", handleKey);
				modal.removeEventListener("click", handleBackdrop);
			};

			const handleSubmit = () => {
				const title = input.value.trim();
				if (!title || title.length < 3) {
					if (errorDiv) {
						errorDiv.textContent = title
							? "Name must be at least 3 characters"
							: "Please enter a family name";
						errorDiv.style.display = "block";
					}
					return;
				}
				hideModal();
				cleanup();
				resolve(title);
			};

			const handleCancel = () => {
				hideModal();
				cleanup();
				resolve(undefined);
			};

			const handleKey = (e: KeyboardEvent) => {
				if (e.key === "Enter")  { e.preventDefault(); handleSubmit(); }
				if (e.key === "Escape") { handleCancel(); }
			};

			const handleBackdrop = (e: MouseEvent) => {
				if (e.target === modal) handleCancel();
			};

			submitBtn?.addEventListener("click", handleSubmit);
			cancelBtn?.addEventListener("click", handleCancel);
			closeBtn?.addEventListener("click", handleCancel);
			input.addEventListener("keydown", handleKey);
			modal.addEventListener("click", handleBackdrop);
		});
	}

	private async saveDesign(
		modelStateId: string,
		url: string,
		designTitle: string,
		imageUrl?: string,
		originalModelStateId?: string | null,
		silent: boolean = false,
	): Promise<void> {
		const saveFormData = new FormData();
		saveFormData.append("action", "save_shapediver_model_state");
		saveFormData.append("modelStateId", modelStateId);
		saveFormData.append("url", url);
		saveFormData.append("designTitle", designTitle);

		const productData = this.productDataManager.getProductData();
		if (productData) {
			saveFormData.append("product_id", productData.id.toString());
			if (productData.variation_id)
				saveFormData.append("variation_id", productData.variation_id.toString());
			if (productData.variation_name)
				saveFormData.append("variation_name", productData.variation_name);
		}

		if (imageUrl) saveFormData.append("imageUrl", imageUrl);

		if (originalModelStateId && originalModelStateId.trim() !== "") {
			saveFormData.append("originalModelStateId", originalModelStateId);
			console.log("🔄 Updating existing design. Original ID:", originalModelStateId, "→ New ID:", modelStateId);
		} else {
			console.log("➕ Inserting new design. ID:", modelStateId);
		}

		const nonce = (window as any).shapediverAjax?.nonce;
		if (nonce) saveFormData.append("nonce", nonce);

		const tbplUid: string =
			((window as any).shapeDiverTbplUid as string | undefined)?.trim() ||
			new URL(window.location.href).searchParams.get("_tbpl_uid") ||
			"";
		if (tbplUid) saveFormData.append("tbpl_uid", tbplUid);

		try {
			const response = await fetch("/wp-admin/admin-ajax.php", {
				method: "POST",
				credentials: "same-origin",
				body: saveFormData,
			});

			if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

			const saveResult = await response.json();

			if (saveResult.success) {
				const action = saveResult.data?.action || "saved";
				console.log(`✅ Design ${action} successfully`);
				if (!silent) this.showSuccessModal();
			} else {
				console.error("❌ Save failed:", saveResult.data);
			}
		} catch (error) {
			console.error("❌ Save error:", error);
		}
	}

	private showSuccessModal(): void {
		const modal = document.getElementById("shapediver-success-modal");
		if (!modal) { console.error("Success modal not found!"); return; }

		const closeBtn = modal.querySelector(".shapediver-btn-close-success") as HTMLButtonElement;
		const closeXBtn = modal.querySelector(".shapediver-success-close") as HTMLButtonElement;

		modal.style.display = "flex";

		const hideModal = () => { modal.style.display = "none"; cleanup(); };

		const handleBackdropClick = (e: MouseEvent) => { if (e.target === modal) hideModal(); };
		const handleKeyPress = (e: KeyboardEvent) => { if (e.key === "Escape") hideModal(); };

		const cleanup = () => {
			if (closeBtn) closeBtn.removeEventListener("click", hideModal);
			if (closeXBtn) closeXBtn.removeEventListener("click", hideModal);
			modal.removeEventListener("click", handleBackdropClick);
			document.removeEventListener("keydown", handleKeyPress);
		};

		if (closeBtn) closeBtn.addEventListener("click", hideModal);
		if (closeXBtn) closeXBtn.addEventListener("click", hideModal);
		modal.addEventListener("click", handleBackdropClick);
		document.addEventListener("keydown", handleKeyPress);
	}
}

// ============================================================================
// DEFAULT — SDK FACTORY REGISTRATION
// ============================================================================

(globalThis as {[key: string]: any}).specificECommerceApiActionsFactory = (
	defaultActions: IECommerceApiActions,
) => {
	globalApiActionsInstance = new SpecificECommerceApiActions(defaultActions);
	return globalApiActionsInstance;
};

let globalApiActionsInstance: SpecificECommerceApiActions | null = null;

const urlParams = new URLSearchParams(window.location.search);

export const developmentUrlBuilderOptions: IAppBuilderUrlBuilderData = {
	baseUrl: `https://appbuilder.shapediver.com/v1/main/${urlParams.get("appBuilderVersion") ?? "latest"}/`,
	settingsUrl: urlParams.get(QUERYPARAM_SETTINGSURL) ?? "_stringselect.json",
	modelStateId: urlParams.get(QUERYPARAM_MODELSTATEID) ?? undefined,
	slug: urlParams.get(QUERYPARAM_SLUG) ?? undefined,
};

console.log("🏗️ developmentUrlBuilderOptions:", JSON.stringify(developmentUrlBuilderOptions, null, 2));

(globalThis as {[key: string]: any}).developmentUrlBuilderOptions =
	developmentUrlBuilderOptions;

// ============================================================================
// ============================================================================
// CUSTOM CODE — everything below this line is custom to Tara Blooms
// ============================================================================
// ============================================================================

// ============================================================================
// CUSTOM — TYPE DEFINITIONS
// ============================================================================

interface ShapeDiverProductData {
	id: number;
	variation_id?: number;
	name: string;
	variation_name?: string;
	price: string;
	regular_price?: string;
	setup_cost?: string;
	model_view_url: string;
	embedding_ticket: string;
	configurator_url: string;
	model_state_id: string;
	slug: string;
	settings_url: string;
	query_params?: Record<string, string>;
}

interface AjaxResponse<T = any> {
	success: boolean;
	data: T;
}

interface ConfiguratorData {
	ajaxurl: string;
	settings: {
		configurator_url: string;
		default_settings_url: string;
		debug_flag: boolean;
		cart_item_button_label: string;
		cart_item_button_classes: string;
		enable_fixed_price?: string;
		sales_tax?: string;            // localized from get_option('sales_tax', '0')
	};
	isLoggedIn: boolean;
	loginUrl: string;
	api_base_url: string;
}

// The API's pricing object nests the base price under `product_price`, and
// groups every extra charge under `costs` / every reduction under
// `discounts`. Both are open-ended string-keyed numeric maps. Flat legacy
// fields are kept optional for backward compatibility.
interface PricingCostsMap {
	[costName: string]: number;
}

interface PricingDiscountsMap {
	[discountName: string]: number;
}

interface PricingSummary {
	product_price?: {
		base_price: number;
		[key: string]: number;
	};
	costs?: PricingCostsMap;
	discounts?: PricingDiscountsMap;
	total_cost: number;
	// Legacy/flat fallback fields (older API shape):
	base_price?: number;
	tariff_cost?: number;
	shipping_cost?: number;
	sales_tax?: number;
	[key: string]: any;
}

interface WarehouseLineItem {
	name?: string;
	sku?: string;
	color?: string;
	color_id?: string;
	color_name?: string;
	color_slug?: string;
	finish?: string;
	finish_id?: string;
	finish_name?: string;
	finish_slug?: string;
	size?: string;
	order_quantity?: number;
	rate?: number;         // per-unit price shown in the RATE column
	unit_price?: number;   // alias of rate for renderers that read unit_price
	markup_price_breakdown?: {
		base_price?: number;
		total_cost?: number;
		rate?: number;
		unit_price?: number;
		[key: string]: any;
	};
	services?: WarehouseLineItem[];
}

interface WarehouseData {
	warehouse_code: string;
	warehouse_name: string;
	availability_status: string;
	available_quantity: number;
	promise_date: string;
	display_message: string;
	pricing_summary: PricingSummary;
	markup_pricing_summary: PricingSummary;
	items?: WarehouseLineItem[];
}

// ============================================================================
// CUSTOM — GLOBAL DECLARATIONS
// ============================================================================

declare const jQuery: any;
declare const configuratorData: ConfiguratorData;

declare global {
	function openWarehousePopup(): void;
	function closeWarehousePopup(): void;
	function addWarehouseToCart(warehouse: string): void;
}

// ============================================================================
// CUSTOM — CONSTANTS & CONFIGURATION
// ============================================================================

const CATEGORY_IDENTIFIER = "\u200B\u2063";
const DEFAULT_PAGE_SIZE = 10;
const API_TIMEOUT = 10000;
const MAX_RETRIES = 2;
const RETRY_DELAY = 2000;

/**
 * Sales tax rate for fixed-price mode, read from PHP's
 * get_option('sales_tax', '0') — localized onto
 * configuratorData.settings.sales_tax (a percentage string, e.g. "10.75").
 * Falls back to 0 if unset/blank/non-numeric so pricing never throws.
 */
function getFixedPriceSalesTaxRate(): number {
	const raw = (window as any).configuratorData?.settings?.sales_tax;
	const pct = parseFloat(raw);
	return Number.isFinite(pct) ? pct / 100 : 0;
}

/**
 * RATE column fix.
 *
 * wrapper_pricing only returns the line price, so every rate came through
 * as 0. This stamps a rate onto every item and service: an explicit
 * rate/unit_price if the API already sent one, otherwise line price ÷ qty.
 *
 * Works on a deep copy — the byte-exact raw snapshot is never touched.
 */
function getLineItemRate(item: any): number {
	if (!item || typeof item !== "object") return 0;
	const breakdown = item.markup_price_breakdown || item.price_breakdown || {};
	const qty = Math.max(1, Number(item.order_quantity ?? item.quantity ?? 1) || 1);

	const explicit = [breakdown.rate, breakdown.unit_price, item.rate, item.unit_price]
		.map((v) => Number(v))
		.find((v) => Number.isFinite(v) && v > 0);
	if (explicit !== undefined) return explicit;

	const lineTotal = Number(
		breakdown.total_cost ?? item.total_cost ?? breakdown.base_price ?? item.price ?? 0,
	) || 0;
	return Math.round((lineTotal / qty) * 100) / 100;
}

function applyRateToLineItem(item: any): any {
	if (!item || typeof item !== "object") return item;
	const rate = getLineItemRate(item);
	item.rate = rate;
	item.unit_price = rate;
	["markup_price_breakdown", "price_breakdown"].forEach((key) => {
		if (item[key] && typeof item[key] === "object") {
			item[key].rate = rate;
			item[key].unit_price = rate;
		}
	});
	if (!item.markup_price_breakdown) {
		item.markup_price_breakdown = {rate, unit_price: rate};
	}
	if (Array.isArray(item.services)) {
		item.services = item.services.map(applyRateToLineItem);
	}
	return item;
}

function withLineItemRates<T extends {items?: any[]}>(warehouse: T): T {
	if (!warehouse || !Array.isArray(warehouse.items)) return warehouse;
	const copy = JSON.parse(JSON.stringify(warehouse));
	copy.items = copy.items.map(applyRateToLineItem);
	return copy;
}

function moveCutoutFlagsToQueryParams(qp?: Record<string, string>): void {
	if (!qp?._parameters_settings_url) return;

	let url = qp._parameters_settings_url;
	const flags: Array<[string, string]> = [
		["CUTOUT_PRODUCT", "_allow_cutouts_product"],
		["CUTOUT_BASES", "_allow_cutouts_bases"],
	];

	for (const [marker, key] of flags) {
		// matches the raw marker (with/without underscore) or an already-inlined key=true
		const re = new RegExp(`([?&])(?:_?${marker}|${key}=true)(?=&|$)`, "g");
		const next = url.replace(re, "$1");
		if (next !== url) {
			url = next;
			qp[key] = "true"; // added after existing keys, so after _context
		}
	}

	url = url.replace(/\?&/g, "?").replace(/&&/g, "&").replace(/[&?]+$/, "");
	qp._parameters_settings_url = url;
}

/**
 * Clean an App Builder iframe URL:
 *  - strips CUTOUT markers from the nested _parameters_settings_url
 *  - appends them as their own outer params, after _context
 */
function fixIframeUrl(src: string): string {
	if (!src || !/cutout/i.test(src)) return src;
	try {
		const u = new URL(src, window.location.href);
		const nested = u.searchParams.get("_parameters_settings_url");
		if (!nested) return src;

		const qp: Record<string, string> = {_parameters_settings_url: nested};
		moveCutoutFlagsToQueryParams(qp);

		u.searchParams.set("_parameters_settings_url", qp._parameters_settings_url);

		// Move _context to the end, then add the flags after it
		const ctx = u.searchParams.get("_context");
		if (ctx !== null) {
			u.searchParams.delete("_context");
			u.searchParams.set("_context", ctx);
		}
		if (qp._allow_cutouts_product) u.searchParams.set("_allow_cutouts_product", "true");
		if (qp._allow_cutouts_bases) u.searchParams.set("_allow_cutouts_bases", "true");

		const fixed = u.toString();
		console.log("🔧 iframe src fixed:", fixed);
		return fixed;
	} catch (e) {
		console.warn("⚠️ fixIframeUrl failed, using original src:", e);
		return src;
	}
}

function installGlobalIframeSrcFixer(): void {
	if ((window as any).__sdIframeFixerInstalled) return;
	(window as any).__sdIframeFixerInstalled = true;

	const isTarget = (el: any): boolean =>
		el instanceof HTMLIFrameElement && el.id === "app-builder-iframe";

	// 1) iframe.src = "..."
	const desc = Object.getOwnPropertyDescriptor(HTMLIFrameElement.prototype, "src");
	if (desc?.get && desc?.set) {
		Object.defineProperty(HTMLIFrameElement.prototype, "src", {
			configurable: true,
			enumerable: desc.enumerable,
			get() { return desc.get!.call(this); },
			set(value: string) {
				desc.set!.call(this, isTarget(this) ? fixIframeUrl(value) : value);
			},
		});
	}

	// 2) iframe.setAttribute("src", "...")
	const origSetAttribute = Element.prototype.setAttribute;
	Element.prototype.setAttribute = function (this: Element, name: string, value: string) {
		if (isTarget(this) && name.toLowerCase() === "src") value = fixIframeUrl(value);
		return origSetAttribute.call(this, name, value);
	};

	// 3) iframes created through innerHTML / template strings
	const repair = (el: Element) => {
		if (!isTarget(el)) return;
		const current = el.getAttribute("src") || "";
		const fixed = fixIframeUrl(current);
		if (fixed !== current) origSetAttribute.call(el, "src", fixed);
	};
	new MutationObserver((mutations) => {
		for (const m of mutations) {
			if (m.type === "attributes") repair(m.target as Element);
			m.addedNodes.forEach((n) => {
				if (n.nodeType !== 1) return;
				repair(n as Element);
				(n as Element).querySelectorAll?.("iframe#app-builder-iframe").forEach(repair);
			});
		}
	}).observe(document.documentElement, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: ["src"],
	});

	console.log("✅ global iframe src fixer installed");
}

// Run at module load, not inside DOMContentLoaded
installGlobalIframeSrcFixer();

class ProductDataManager {
	private static instance: ProductDataManager;
	private productData: ShapeDiverProductData | null = null;
	private loadingPromise: Promise<ShapeDiverProductData | null> | null = null;

	private constructor() {}

	public static getInstance(): ProductDataManager {
		if (!ProductDataManager.instance) {
			ProductDataManager.instance = new ProductDataManager();
		}
		return ProductDataManager.instance;
	}

	public getProductId(): number | null {
		if (this.productData?.id) return this.productData.id;

		const windowProduct = (window as any).shapeDiverCurrentProduct;
		if (windowProduct?.id) return windowProduct.id;

		const windowProductId = (window as any).shapeDiverProductId;
		if (windowProductId) return parseInt(windowProductId);

		const modal = document.getElementById("app-builder-modal-wrapper");
		if (modal && (modal as any).currentProductData?.id)
			return (modal as any).currentProductData.id;

		const button = document.getElementById("app-builder-open-configurator");
		const productId = button?.getAttribute("data-product-id");
		if (productId) return parseInt(productId);

		return null;
	}

	public getProductData(): ShapeDiverProductData | null {
		if (this.productData) return this.productData;

		const windowProduct = (window as any).shapeDiverCurrentProduct;
		if (windowProduct?.id) {
			this.productData = windowProduct;
			return this.productData;
		}

		const modal = document.getElementById("app-builder-modal-wrapper");
		if (modal && (modal as any).currentProductData) {
			this.productData = (modal as any).currentProductData;
			return this.productData;
		}

		return null;
	}

	public async loadProductData(
		productId: string | number,
		variationId?: string | number,
	): Promise<ShapeDiverProductData | null> {
		if (this.loadingPromise) return this.loadingPromise;

		this.loadingPromise = this._fetchProductData(productId, variationId);
		const result = await this.loadingPromise;
		this.loadingPromise = null;
		return result;
	}

	private async _fetchProductData(
		productId: string | number,
		variationId?: string | number,
	): Promise<ShapeDiverProductData | null> {
		try {
			console.log(
				"🔄 Loading product data for ID:", productId,
				variationId ? `(variation ${variationId})` : "(no variation)",
			);

			const body: Record<string, string> = {
				action: "get_product_data",
				product_id: productId.toString(),
			};
			if (variationId) body.variation_id = variationId.toString();

			const response = await fetch(configuratorData.ajaxurl, {
				method: "POST",
				headers: {"Content-Type": "application/x-www-form-urlencoded"},
				body: new URLSearchParams(body),
			});

			if (!response.ok) throw new Error(`HTTP ${response.status}`);

			const result: AjaxResponse<ShapeDiverProductData> = await response.json();

			if (result.success && result.data) {
				this.setProductData(result.data);
				console.log("✅ Product data loaded:", result.data.name, "| regular_price:", result.data.regular_price, "| setup_cost:", result.data.setup_cost);
				console.log("🔗 query_params from server:", JSON.stringify(result.data.query_params, null, 2));

				return result.data;
			} else {
				throw new Error("Invalid response from server");
			}
		} catch (error) {
			console.error("❌ Failed to load product data:", error);
			return null;
		}
	}

public setProductData(productData: ShapeDiverProductData): void {
	moveCutoutFlagsToQueryParams(productData.query_params);

	this.productData = productData;
	(window as any).shapeDiverCurrentProduct = productData;
	(window as any).shapeDiverProductId = productData.id;

	const modal = document.getElementById("app-builder-modal-wrapper");
	if (modal) (modal as any).currentProductData = productData;

	console.log("💾 Product data stored (ID:", productData.id, ")");
}

	public clear(): void {
		this.productData = null;
		this.loadingPromise = null;
		delete (window as any).shapeDiverCurrentProduct;
		delete (window as any).shapeDiverProductId;

		const modal = document.getElementById("app-builder-modal-wrapper");
		if (modal) delete (modal as any).currentProductData;
	}
}

// ============================================================================
// GRAPHICS API MANAGER (Singleton)
// ============================================================================

class GraphicsApiManager {
	private static instance: GraphicsApiManager;
	private latestQuery: string = "";
	private latestCategorySlugs: Array<string> = [];
	private pageSize: number = DEFAULT_PAGE_SIZE;
	private currentIndex: number = 0;
	private cachedResults: Record<string, IGraphicsApiResponse> = {};
	private resetCacheCallback: (() => void) | null = null;

	private constructor() {
		this.latestQuery = this.createQueryString();
	}

	public static getInstance(): GraphicsApiManager {
		if (!GraphicsApiManager.instance) {
			GraphicsApiManager.instance = new GraphicsApiManager();
		}
		return GraphicsApiManager.instance;
	}

	public createQueryString(
		categories?: string[] | string,
		tags?: string,
		search?: string,
		categoryIds?: number[],
	): string {
		const params = new URLSearchParams();

		if (Array.isArray(categories) && categories.length === 0)
			categories = undefined;
		if (!categories && !tags && !search) categories = "all";

		if (categoryIds && categoryIds.length > 0) {
			params.set("category_id", categoryIds[categoryIds.length - 1].toString());
			if (!tags && !search) params.set("tags", "all");
		} else if (categories) {
			if (Array.isArray(categories))
				params.set("categories", categories.join(","));
			else params.set("categories", categories);
			if (categories !== "all" && !tags && !search)
				params.set("tags", "all");
		}

		if (tags) params.set("tags", tags);
		if (search) params.set("search", search);

		return params.toString();
	}

	public async fetchFromApi(
		queryString: string,
		ignoreCache: boolean = false,
	): Promise<IGraphicsApiResponse> {
		if (!ignoreCache && this.cachedResults[queryString])
			return this.cachedResults[queryString];

		const url = buildGraphicsApiUrl(queryString);
		const nonce = await getFreshNonce();

		const resp = await fetch(url, {
			method: "GET",
			credentials: "include",
			headers: {
				Accept: "application/json",
				"X-WP-Nonce": nonce,
			},
		});

		if (!resp.ok) {
			const text = await resp.text().catch(() => "");
			throw new Error(
				`Failed to fetch from graphics api: ${resp.status} ${resp.statusText} ${text}`,
			);
		}

		const json = await resp.json().catch((err) => {
			throw new Error(`Failed to parse graphics api response: ${String(err)}`);
		});

		if (!json || typeof json !== "object")
			throw new Error("Invalid graphics api response: not an object");

		const result = json as IGraphicsApiResponse;
		this.cachedResults[queryString] = result;
		return result;
	}

	public clearCache(): void {
		this.latestQuery = this.createQueryString();
		this.latestCategorySlugs = [];
		this.pageSize = DEFAULT_PAGE_SIZE;
		this.currentIndex = 0;

		for (const key in this.cachedResults) delete this.cachedResults[key];
		if (this.resetCacheCallback) this.resetCacheCallback();
	}

	public async setParameters(
		data: IScrollingApiSetParametersData,
	): Promise<IScrollingApiSetParametersReply<unknown>> {
		if (data.source !== "graphics") {
			console.debug(`Unsupported data source name: ${data.source}`);
			this.clearCache();
			return {hasNextPage: false, items: []};
		}

		if (data.reset) this.resetCacheCallback = data.reset;

		// FIX: same as the module-level scrollingApiSetParameters() — a
		// call with no terms means the widget just mounted fresh, so bring
		// our navigation state back to the Art root.
		if (!data.terms || data.terms.length === 0) {
			this.latestQuery = this.createQueryString();
			this.latestCategorySlugs = [];
			this.currentIndex = 0;
		}

		if (data.terms) {
			const categories: string[] = [];
			const categoryIds: number[] = [];
			let tags: string = "";
			let search: string = "";

			const allCategoriesQuery = this.createQueryString();
			const categoryData = await this.fetchFromApi(allCategoriesQuery, false);
			let cachedCategories = categoryData.categories;

			data.terms?.forEach((v: string) => {
				if (v.startsWith(CATEGORY_IDENTIFIER)) {
					if (cachedCategories) {
						const categoryName = v.substring(CATEGORY_IDENTIFIER.length);
						const matchedCategory: ICategory | undefined = cachedCategories.find(
							(cat) => cat.name === categoryName,
						);
						if (matchedCategory) {
							categories.push(matchedCategory.slug);
							categoryIds.push(matchedCategory.id);
							cachedCategories = matchedCategory.children;
						}
					}
				} else if (v.startsWith("tag:")) {
					tags = v.substring("tag:".length);
				} else {
					search = v;
				}
			});

			const queryString = this.createQueryString(categories, tags, search, categoryIds);
			if (queryString !== this.latestQuery) {
				this.latestQuery = queryString;
				this.latestCategorySlugs = categories;
				this.currentIndex = 0;
			}
		}

		if (data.pageSize !== undefined) {
			this.pageSize = data.pageSize;
			this.currentIndex = 0;
		}

		await this.fetchFromApi(this.latestQuery, false);
		return this.returnMappedResults();
	}

	public async loadMore(
		data: IScrollingApiLoadMoreData,
	): Promise<IScrollingApiLoadMoreReply<unknown>> {
		if (data.source !== "graphics") {
			console.debug(`Unsupported data source name: ${data.source}`);
			this.clearCache();
			return {hasNextPage: false, items: []};
		}

		await this.fetchFromApi(this.latestQuery, false);
		return this.returnMappedResults();
	}

	private async returnMappedResults(): Promise<IScrollingApiLoadMoreReply<unknown>> {
		const result: IScrollingApiLoadMoreReply<unknown> = {
			hasNextPage: false,
			items: [],
		};

		const isSearchMode      = isSearchQuery(this.latestQuery);
		const shouldShowFolders = this.currentIndex === 0 && !isSearchMode;

		if (shouldShowFolders) {
			const allCategoriesQuery = this.createQueryString();
			const categoryData = await this.fetchFromApi(allCategoriesQuery, false);
			let categories = categoryData.categories;

			if (categories) {
				for (let i = 0; i < this.latestCategorySlugs.length; i++) {
					const categorySlug = this.latestCategorySlugs[i];
					const matchedCategory: ICategory | undefined = categories?.find(
						(cat) => cat.slug === categorySlug,
					);
					if (!matchedCategory)
						throw new Error(`Category with slug "${categorySlug}" not found`);
					categories = matchedCategory.children;
				}

				const items: IScrollingApiItemTypeSelect[] = categories.map((c) => ({
					item: `search:${CATEGORY_IDENTIFIER}` + c.name,
					data: {
						displayname: c.name,
						imageUrl: c.thumbnail_url,
						data: c,
					},
				}));
				result.items = items;
			}
		}

		// FIX: don't advance currentIndex for a defined-but-empty products
		// array (folder-only categories).
		const products = this.cachedResults[this.latestQuery]?.products;
		if (products && products.length > 0) {
			const items: IScrollingApiItemTypeSelect[] = products
				.slice(this.currentIndex, this.currentIndex + this.pageSize)
				.map((p) => ({
					item: p.graphic_id + "",
					data: {
						displayname: getArtworkTileLabel(p, isSearchMode),
						tooltip: p.description,
						imageUrl:
							p.downloadable_files?.find((f) => f.sessionId?.includes("thumbnail"))
								?.parameterValues.file_url
							?? p.thumbnail_url
							?? p.image_url,
						data: p,
					},
				}));

			this.currentIndex += this.pageSize;
			result.hasNextPage = this.currentIndex < products.length;
			result.items = result.items.concat(items);
		}

		return result;
	}
}

// ============================================================================
// SETTINGS MANAGER (Singleton)
// ============================================================================

class SettingsManager {
	private static instance: SettingsManager;
	private settings: Record<string, string> = {};
	private loadPromise: Promise<void> | null = null;

	private constructor() {}

	public static getInstance(): SettingsManager {
		if (!SettingsManager.instance) {
			SettingsManager.instance = new SettingsManager();
		}
		return SettingsManager.instance;
	}

	public async load(): Promise<void> {
		if (this.loadPromise) return this.loadPromise;

		this.loadPromise = fetch(configuratorData.ajaxurl, {
			method: "POST",
			headers: {"Content-Type": "application/x-www-form-urlencoded"},
			body: new URLSearchParams({action: "get_shapediver_settings"}),
		})
			.then((res) => res.json())
			.then((result) => {
				if (result.success && result.data) {
					this.settings = result.data;
					console.log("✅ ShapeDiver settings loaded:", this.settings);
				}
			})
			.catch((err) => {
				console.warn("⚠️ Failed to load ShapeDiver settings, using defaults:", err);
			});

		return this.loadPromise;
	}

	public get(key: string, fallback: string = ""): string {
		return this.settings[key] ?? fallback;
	}

	public getPreferredWarehouse(): string {
		return this.get("preferred_warehouse", "COL");
	}
}

// ============================================================================
// MODAL MANAGER (Singleton)
// ============================================================================

class ShapeDiverModalManager {
	private static instance: ShapeDiverModalManager | null = null;
	private modal: HTMLElement | null = null;
	private closeButton: HTMLElement | null = null;
	private iframe: HTMLIFrameElement | null = null;
	private eventListeners: Map<string, EventListenerOrEventListenerObject> = new Map();

	private constructor() {
		this.init();
	}

	public static getInstance(): ShapeDiverModalManager {
		if (!ShapeDiverModalManager.instance) {
			ShapeDiverModalManager.instance = new ShapeDiverModalManager();
		}
		return ShapeDiverModalManager.instance;
	}

	private init(): void {
		if (document.readyState === "loading") {
			document.addEventListener("DOMContentLoaded", () => this.setupElements());
		} else {
			this.setupElements();
		}
	}

	private setupElements(): void {
		this.modal = document.getElementById("app-builder-modal-wrapper");
		this.closeButton = document.getElementById("app-builder-modal-close-button");
		this.iframe = document.getElementById("shapediver-iframe-model") as HTMLIFrameElement;

		if (!this.modal || !this.closeButton) {
			console.warn("⚠️ Modal elements not found");
			return;
		}

		this.setupEventListeners();
		console.log("✅ Modal manager initialized");
	}

	private setupEventListeners(): void {
		if (!this.modal || !this.closeButton) return;

		this.removeEventListeners();

		const closeHandler = () => this.closeModal();
		this.closeButton.addEventListener("click", closeHandler);
		this.eventListeners.set("closeButton", closeHandler);

		const backdropHandler = (e: Event) => {
			if (e.target === this.modal) this.closeModal();
		};
		this.modal.addEventListener("click", backdropHandler);
		this.eventListeners.set("backdrop", backdropHandler);

		const escHandler = (e: Event) => {
			if ((e as KeyboardEvent).key === "Escape" && this.isModalOpen())
				this.closeModal();
		};
		document.addEventListener("keydown", escHandler);
		this.eventListeners.set("escape", escHandler);
	}

	private removeEventListeners(): void {
		if (this.closeButton && this.eventListeners.has("closeButton")) {
			this.closeButton.removeEventListener(
				"click",
				this.eventListeners.get("closeButton")!,
			);
		}
		if (this.modal && this.eventListeners.has("backdrop")) {
			this.modal.removeEventListener(
				"click",
				this.eventListeners.get("backdrop")! as EventListenerOrEventListenerObject,
			);
		}
		if (this.eventListeners.has("escape")) {
			document.removeEventListener(
				"keydown",
				this.eventListeners.get("escape")! as EventListenerOrEventListenerObject,
			);
		}
		this.eventListeners.clear();
	}

	private isModalOpen(): boolean {
		return this.modal ? this.modal.style.display !== "none" : false;
	}

	public async closeModal(): Promise<void> {
		console.log("🚪 Closing ShapeDiver modal");

		if (globalApiActionsInstance) {
			console.log("💾 Triggering closeConfigurator for auto-save...");
			try {
				await globalApiActionsInstance.closeConfigurator();
				console.log("✅ closeConfigurator completed");
			} catch (err) {
				console.warn("⚠️ closeConfigurator failed:", err);
			}
		} else {
			console.warn("⚠️ No globalApiActionsInstance — save skipped");
		}

		if (typeof (window as any).destroySpecificECommerceApiActions === "function") {
			(window as any).destroySpecificECommerceApiActions();
		}

		if (this.modal) this.modal.style.display = "none";
		if (this.iframe) this.iframe.src = "about:blank";

		// ── Clear ALL per-session globals, not just family name ────────────
		ShapeDiverModalManager.clearSessionGlobals();
		console.log("✅ Modal closed, session globals cleared");
	}

	public static clearSessionGlobals(): void {
		delete (window as any).shapeDiverFamilyName;
		delete (window as any).shapeDiverImageUrl;
		delete (window as any).shapeDiverApiResponse;
		delete (window as any).shapeDiverTbplUid;
		ProductDataManager.getInstance().clear();
	}

	public openModal(productData?: ShapeDiverProductData): void {
		console.log("🚀 Opening ShapeDiver modal");

		if (!this.modal) {
			console.error("❌ Modal not found");
			return;
		}

		// ── Defensive clear on open too ─────────────────────────────────────
		ShapeDiverModalManager.clearSessionGlobals();

		if (productData) ProductDataManager.getInstance().setProductData(productData);
		this.modal.style.display = "flex";
	}
}

// ============================================================================
// WAREHOUSE MANAGER
// ============================================================================

class WarehouseManager {
	private static formatCurrency(amount: number): string {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 2,
		}).format(amount);
	}

	// Sum every numeric value in a flat object (costs / discounts).
	private static sumNumericValues(map?: Record<string, any>): number {
		if (!map) return 0;
		return Object.values(map).reduce((sum: number, value) => {
			const num = typeof value === "number" ? value : parseFloat(value);
			return sum + (Number.isFinite(num) ? num : 0);
		}, 0);
	}

	// Normalizes a pricing summary (whichever shape it comes in) into the
	// numbers the UI needs.
	private static getPricingBreakdown(pricing: PricingSummary): {
		productCost: number;
		otherCosts: number;
		discountsTotal: number;
		subTotal: number;
		grandTotal: number;
	} {
		const productCost =
			pricing.product_price?.base_price ?? pricing.base_price ?? 0;

		const otherCosts = pricing.costs
			? this.sumNumericValues(pricing.costs)
			: (pricing.tariff_cost || 0) +
			(pricing.shipping_cost || 0) +
			(pricing.sales_tax || 0);

		const discountsTotal = this.sumNumericValues(pricing.discounts);

		const subTotal = productCost + otherCosts;
		const grandTotal = subTotal - discountsTotal;

		return {productCost, otherCosts, discountsTotal, subTotal, grandTotal};
	}

	private static formatDate(dateString: string): string {
		try {
			const [year, month, day] = dateString.split("-");
			const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
			const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
			return `${day}-${months[date.getMonth()]}-${year}`;
		} catch (e) {
			return dateString;
		}
	}

	// Used for sorting
	private static parsePromiseDate(dateString: string): Date {
		const [year, month, day] = dateString.split("-").map((num) => parseInt(num, 10));
		return new Date(year, month - 1, day);
	}

	public static async openPopup(
		warehouses: WarehouseData[],
		pricingParameters: Record<string, any>,
		modelStateId: string,
		productData?: ShapeDiverProductData | null,
		apiResponse?: any,
		rawWarehousesByCode?: Record<string, any>,
	): Promise<void> {

		console.log("🛒 catalogMode check:", (window as any).shapediverAjax?.catalogMode);

		await SettingsManager.getInstance().load();
		const PREFERRED_WAREHOUSE = SettingsManager.getInstance().getPreferredWarehouse();

		console.log("🏬 Opening warehouse popup. Preferred warehouse:", PREFERRED_WAREHOUSE);

		if (!pricingParameters.product_id) {
			const productId = ProductDataManager.getInstance().getProductId();
			if (productId) {
				pricingParameters.product_id = productId;
				console.log("✅ Added product_id to pricingParameters:", productId);
			} else {
				console.error("❌ No product_id available!");
				alert("Unable to load pricing information. Product ID not found.\n\nPlease refresh the page and try again.");
				return;
			}
		}

		if (productData) ProductDataManager.getInstance().setProductData(productData);

		(window as any).shapeDiverApiResponse = apiResponse;

		const preferredWarehouse = warehouses.find(
			(w) => w.warehouse_code === PREFERRED_WAREHOUSE,
		);

		let warehousesToDisplay: WarehouseData[];

		if (preferredWarehouse && preferredWarehouse.available_quantity > 0) {
			warehousesToDisplay = [preferredWarehouse];
			console.log("✅ Preferred warehouse in stock — showing only:", PREFERRED_WAREHOUSE);
		} else {
			warehousesToDisplay = warehouses;
			console.log("⚠️ Preferred warehouse out of stock or unavailable — showing all", warehouses.length, "warehouse(s)");
		}

		warehousesToDisplay = warehousesToDisplay.sort((a, b) => {
			const dateA = this.parsePromiseDate(a.promise_date);
			const dateB = this.parsePromiseDate(b.promise_date);
			return dateA.getTime() - dateB.getTime();
		});

		const fastestWarehouse =
			warehousesToDisplay.length > 1 ? warehousesToDisplay[0].warehouse_code : null;
		const cheapestWarehouse =
			warehousesToDisplay.length > 1
				? warehousesToDisplay.reduce((min, w) => {
						const minPrice = this.getPricingBreakdown(
							min.markup_pricing_summary || min.pricing_summary,
						).grandTotal;
						const wPrice = this.getPricingBreakdown(
							w.markup_pricing_summary || w.pricing_summary,
						).grandTotal;
						return wPrice < minPrice ? w : min;
					}).warehouse_code
				: null;

		const popup = document.getElementById("warehousePopup");
		if (!popup) return;

		const container = popup.querySelector(".warehouse-container");
		if (!container) return;

		container.innerHTML = "";

		warehousesToDisplay.forEach((warehouse) => {
			const card = this.createWarehouseCard(
				warehouse,
				pricingParameters,
				modelStateId,
				0,
				warehouse.warehouse_code === PREFERRED_WAREHOUSE,
				warehouse.warehouse_code === fastestWarehouse,
				warehouse.warehouse_code === cheapestWarehouse,
				rawWarehousesByCode?.[warehouse.warehouse_code],
			);
			container.appendChild(card);
		});

		const wrapper = popup.querySelector(".warehouse-popup-wrapper");
		if (wrapper) {
			const isPreferredInStock = preferredWarehouse && preferredWarehouse.available_quantity > 0;
			if (isPreferredInStock) {
				wrapper.classList.add("single-warehouse");
				wrapper.classList.remove("multiple-warehouses");
			} else {
				wrapper.classList.add("multiple-warehouses");
				wrapper.classList.remove("single-warehouse");
			}
		}

		popup.classList.add("active");
		document.body.style.overflow = "hidden";

		setTimeout(() => this.setupCloseHandlers(popup), 100);
		this.setupRealtimePricingButton(apiResponse, modelStateId, productData);
	}

	private static setupRealtimePricingButton(
		apiResponse: any,
		modelStateId: string,
		productData?: ShapeDiverProductData | null,
	): void {
		const button = document.getElementById("enable-realtime-pricing-btn") as HTMLButtonElement;
		const messageDiv = document.getElementById("realtime-message") as HTMLElement;

		if (!button) {
			console.warn("Real-time pricing button not found");
			return;
		}

		const newButton = button.cloneNode(true) as HTMLButtonElement;
		button.parentNode?.replaceChild(newButton, button);

		newButton.addEventListener("click", async () => {
			console.log("⚡ ENABLE REAL-TIME PRICING CLICKED");

			const productId = productData?.id || ProductDataManager.getInstance().getProductId();

			if (!productId) {
				console.error("❌ No product ID available");
				this.showMessage(messageDiv, "Product ID not found. Please try again.", "error");
				return;
			}

			if (!apiResponse) {
				console.error("❌ No API response available");
				this.showMessage(messageDiv, "Pricing data not available. Please try again.", "error");
				return;
			}

			newButton.disabled = true;
			const btnText = newButton.querySelector(".btn-text") as HTMLElement;
			const btnLoading = newButton.querySelector(".btn-loading") as HTMLElement;

			if (btnText) btnText.style.display = "none";
			if (btnLoading) btnLoading.style.display = "flex";

			messageDiv.style.display = "none";
			messageDiv.className = "realtime-message";

			try {
				const response = await fetch(configuratorData.ajaxurl, {
					method: "POST",
					headers: {"Content-Type": "application/x-www-form-urlencoded"},
					credentials: "same-origin",
					body: new URLSearchParams({
						action: "enable_realtime_pricing",
						product_id: productId.toString(),
						model_state_id: modelStateId,
						api_response: JSON.stringify(apiResponse),
					}),
				});

				const result = await response.json();
				console.log("✅ Response:", result);

				if (result.success) {
					this.showMessage(
						messageDiv,
						result.data?.message || "Real-time pricing & stock enabled successfully!",
						"success",
					);
					if (btnText) btnText.textContent = "✓ Real-Time Pricing Enabled";
					setTimeout(() => {
						if (btnText) btnText.textContent = "Enable Real Time Pricing & Stock";
					}, 2000);
				} else {
					throw new Error(result.data?.message || "Failed to enable real-time pricing");
				}
			} catch (error: any) {
				console.error("❌ Error:", error);
				this.showMessage(
					messageDiv,
					error.message || "Failed to enable real-time pricing. Please try again.",
					"error",
				);
			} finally {
				newButton.disabled = false;
				if (btnText) btnText.style.display = "inline";
				if (btnLoading) btnLoading.style.display = "none";
			}
		});

		console.log("✅ Real-time pricing button setup complete");
	}

	private static showMessage(
		element: HTMLElement,
		message: string,
		type: "success" | "error",
	): void {
		element.textContent = message;
		element.className = `realtime-message ${type}`;
		element.style.display = "block";

		if (type === "success") {
			setTimeout(() => {
				element.style.display = "none";
			}, 5000);
		}
	}

	private static setupCloseHandlers(popup: HTMLElement): void {
		const closeBtn = popup.querySelector(".warehouse-popup-close") as HTMLButtonElement;
		if (closeBtn) {
			const newCloseBtn = closeBtn.cloneNode(true) as HTMLButtonElement;
			closeBtn.parentNode?.replaceChild(newCloseBtn, closeBtn);
			newCloseBtn.addEventListener("click", (e) => {
				e.preventDefault();
				e.stopPropagation();
				this.closePopup();
			});
		}

		popup.addEventListener("click", (e: MouseEvent) => {
			if (e.target === popup) this.closePopup();
		});
	}

	public static closePopup(): void {
		const popup = document.getElementById("warehousePopup");
		if (popup) {
			popup.classList.remove("active");
			document.body.style.overflow = "";
		}
	}

	private static createWarehouseCard(
		warehouse: WarehouseData,
		pricingParameters: Record<string, any>,
		modelStateId: string,
		_index: number,
		isPreferred: boolean = false,
		isFastest: boolean = false,
		isCheapest: boolean = false,
		rawWarehouseData?: any,
	): HTMLElement {
		const wrapper = document.createElement("div");
		wrapper.className = "warehouse-card-wrapper";

		const isCatalogMode: boolean =
			(window as any).shapediverAjax?.catalogMode === true;

		if (isFastest || isCheapest) {
			const badgesContainer = document.createElement("div");
			badgesContainer.className = "warehouse-badges-container";

			if (isFastest) {
				const fastestBadge = document.createElement("span");
				fastestBadge.className = "fastest-badge";
				fastestBadge.innerHTML = "⚡FASTEST";
				badgesContainer.appendChild(fastestBadge);
			}

			if (isCheapest) {
				const affordableBadge = document.createElement("span");
				affordableBadge.className = "affordable-badge";
				affordableBadge.innerHTML = "💰 AFFORDABLE";
				badgesContainer.appendChild(affordableBadge);
			}

			wrapper.appendChild(badgesContainer);
		}

		const card = document.createElement("div");
		card.className = "warehouse-card" + (isPreferred ? " preferred-warehouse" : "");

		const formattedDate = this.formatDate(warehouse.promise_date);
		const pricing = warehouse.markup_pricing_summary || warehouse.pricing_summary;

		const isFixedPriceCard: boolean = !!pricing.is_fixed_price;

		const maskPrice = (amount: number): string =>
			isCatalogMode ? "xxx" : this.formatCurrency(amount);

		const {productCost, otherCosts, discountsTotal, grandTotal} =
			this.getPricingBreakdown(pricing);

		const discountsRowHtml =
			discountsTotal > 0
				? `
				<div class="detail-row discount-row">
					<span class="detail-label">Discounts</span>
					<span class="detail-value">-${maskPrice(discountsTotal)}</span>
				</div>`
				: "";

		card.innerHTML = `
			<div class="warehouse-details">
				<div class="detail-row">
					<span class="detail-label">Expected Date</span>
					<span class="detail-value">${formattedDate}</span>
				</div>
				<div class="detail-row">
					<span class="detail-label">Available Quantity</span>
					<span class="detail-value ${warehouse.available_quantity === 0 ? "out-of-stock" : ""}">
						${warehouse.available_quantity}
					</span>
				</div>
				<div class="detail-row">
					<span class="detail-label">Product Cost</span>
					<span class="detail-value">${maskPrice(productCost)}</span>
				</div>
				<div class="detail-row">
					<span class="detail-label">${isFixedPriceCard ? 'Other Costs (Sales Tax, Setting Cost, etc.)' : 'Other Costs (Tariff, Shipping, Taxes, etc.)'}</span>
					<span class="detail-value">${maskPrice(otherCosts)}</span>
				</div>
				${discountsRowHtml}
				<div class="detail-row total-row">
					<span class="detail-label"><strong>Grand Total</strong></span>
					<span class="detail-value"><strong>${maskPrice(grandTotal)}</strong></span>
				</div>
			</div>
			${!isCatalogMode ? `
			<button class="warehouse-add-to-cart-btn" data-warehouse="${warehouse.warehouse_code}">
				ADD TO CART
			</button>
			` : ''}
		`;

		if (!isCatalogMode) {
			const button = card.querySelector(".warehouse-add-to-cart-btn") as HTMLButtonElement;
			if (button) {
				button.addEventListener("click", () => {
					this.addToCart(
						warehouse.warehouse_code,
						warehouse.warehouse_name,
						grandTotal,
						pricingParameters,
						modelStateId,
						warehouse,
						rawWarehouseData,
					);
				});
			}
		}

		wrapper.appendChild(card);
		return wrapper;
	}

	private static async addToCart(
		warehouseCode: string,
		warehouseName: string,
		totalCost: number,
		pricingParameters: Record<string, any>,
		modelStateId: string,
		warehouseData: WarehouseData,
		rawWarehouseData?: any,
	): Promise<void> {
		console.log("🛒 ADD TO CART | Warehouse:", warehouseName, "| Total:", totalCost);

		const productId = pricingParameters.product_id
			? parseInt(pricingParameters.product_id)
			: ProductDataManager.getInstance().getProductId();

		if (!productId) {
			console.error("❌ CRITICAL: No product ID found!");
			alert("Unable to add to cart. Product ID not found.\n\nPlease refresh the page and try again.");
			return;
		}

		const variationId = pricingParameters.variation_id ? parseInt(pricingParameters.variation_id) : 0;
		let imageUrl = (window as any).shapeDiverImageUrl || "";
		let resolvedAttachmentId: number | null = null;
		const apiResponse = (window as any).shapeDiverApiResponse || {};
		const familyName = ((window as any).shapeDiverFamilyName as string | undefined)?.trim() || "";

		// If we still have the raw base64 snapshot, upload it now via the
		// reliable multipart path.
		if (imageUrl && imageUrl.startsWith("data:image")) {
			const uploaded = await uploadSnapshotForCart(imageUrl, modelStateId);
			if (uploaded) {
				imageUrl = uploaded.url;
				resolvedAttachmentId = uploaded.attachmentId;
				(window as any).shapeDiverImageUrl = uploaded.url;
				console.log("✅ Cart snapshot uploaded:", uploaded.url, "| attachment:", uploaded.attachmentId);
			} else {
				console.warn("⚠️ Cart snapshot upload failed — falling back to inline base64 (may be dropped by server if large)");
			}
		}

		// Stamp a per-unit rate on every item/service (see withLineItemRates()).
		const warehouseDataWithRates = withLineItemRates(warehouseData);

		const requestData: Record<string, string> = {
			action: "shapediver_add_warehouse_to_cart",
			product_id: productId.toString(),
			variation_id: variationId.toString(),
			quantity: "1",
			warehouse_code: warehouseCode,
			warehouse_name: warehouseName,
			model_state_id: modelStateId,
			design_family_name: familyName,
			total_price: totalCost.toString(),
			api_response: JSON.stringify(apiResponse),
			selected_warehouse_data: JSON.stringify(warehouseDataWithRates),
			// Byte-exact wrapper_pricing API response for this warehouse.
			selected_warehouse_data_api_raw: JSON.stringify(rawWarehouseData || warehouseData),
			image_url: imageUrl,
			shapediver_attachment_id: resolvedAttachmentId ? resolvedAttachmentId.toString() : "",
			custom_data: JSON.stringify({
				warehouse_code: warehouseCode,
				warehouse_name: warehouseName,
				model_state_id: modelStateId,
				api_response: apiResponse,
				selected_warehouse: warehouseDataWithRates,
			}),
			sd_configurator_url:          pricingParameters.sd_configurator_url          || "",
			sd_slug:                      pricingParameters.sd_slug                      || "",
			sd_settings_url:              pricingParameters.sd_settings_url              || "",
			sd_parameters_settings_url:   pricingParameters.sd_parameters_settings_url   || "",
		};

		const clickedButton = document.querySelector(
			`[data-warehouse="${warehouseCode}"]`,
		) as HTMLButtonElement;
		if (clickedButton) {
			clickedButton.disabled = true;
			clickedButton.textContent = "ADDING...";
		}

		try {
			const response = await fetch(configuratorData.ajaxurl, {
				method: "POST",
				headers: {"Content-Type": "application/x-www-form-urlencoded"},
				credentials: "same-origin",
				body: new URLSearchParams(requestData),
			});

			const result = await response.json();
			console.log("✅ Response:", result);

			if (result.success) {
				this.closePopup();
				const cartUrl = result.data?.cart_url || "/cart/";
				const cartTab = window.open(cartUrl, "_blank");
				if (!cartTab) {
					window.location.href = cartUrl;
				}
				console.log("✅ Cart opened in new tab");
			} else {
				console.error("❌ Failed:", result);
				alert("Failed to add to cart: " + (result.data?.message || result.message || "Unknown error"));
			}
		} catch (error) {
			console.error("❌ Error:", error);
			alert("Network error. Please try again.");
		} finally {
			if (clickedButton) {
				clickedButton.disabled = false;
				clickedButton.textContent = "ADD TO CART";
			}
		}
	}
}



// ============================================================================
// IMAGE UPLOAD
// ============================================================================

async function imageUpload(): Promise<boolean> {
	console.log("🖼️ IMAGE UPLOAD TRIGGERED");

	return new Promise((resolve) => {
		const modal = createImageUploadModal();
		document.body.appendChild(modal);

		setTimeout(() => {
			modal.style.display = "flex";
		}, 10);

		setupImageUploadHandlers(modal, resolve);
	});
}

function createImageUploadModal(): HTMLElement {
	const modal = document.createElement("div");
	modal.id = "shapediver-image-upload-modal";
	modal.className = "shapediver-upload-modal";

	modal.innerHTML = `
		<div class="shapediver-upload-content">
			<div class="shapediver-upload-header">
				<h3>Upload Image</h3>
				<button class="shapediver-upload-close" aria-label="Close">&times;</button>
			</div>
			<div class="shapediver-upload-body">
				<div class="upload-form-group">
					<label for="image-type-select">
						Art Type <span class="required">*</span>
						<span class="upload-tooltip-wrapper">
							<span class="upload-tooltip-icon" aria-label="Art type information" tabindex="0">?</span>
						</span>
					</label>
					<select id="image-type-select" class="upload-type-select" required>
						<option value="flat_carve" selected>Flat Carve</option>
						<option value="flat_shape_carve_addl">Shape Carve</option>
						<option value="laser_scene">Laser Scene</option>
						<option value="laser_spot">Laser Spot</option>
						<option value="porcelain_portrait">Porcelain Portrait</option>
					</select>
					<div class="upload-error-message" style="display: none;"></div>
				</div>
				<div class="upload-form-group">
					<label>Image File <span class="required">*</span></label>
					<div class="upload-dropzone" id="image-dropzone">
						<input type="file" id="image-file-input" accept="image/jpeg,image/png,image/gif,image/webp" style="display: none;">
						<div class="upload-dropzone-content">
							<svg class="upload-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
								<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
								<polyline points="17 8 12 3 7 8"></polyline>
								<line x1="12" y1="3" x2="12" y2="15"></line>
							</svg>
							<p class="upload-text">Drag & drop your image here</p>
							<p class="upload-subtext">or</p>
							<button type="button" class="upload-browse-btn">Browse Files</button>
							<p class="upload-hint">Supported formats: JPEG, PNG, GIF, WebP (Max 10MB)</p>
						</div>
						<div class="upload-file-preview" style="display: none;">
							<img class="upload-preview-image" src="" alt="Preview">
							<div class="upload-file-info">
								<p class="upload-file-name"></p>
								<p class="upload-file-size"></p>
							</div>
							<button type="button" class="upload-remove-btn">&times;</button>
						</div>
					</div>
				</div>
			</div>
			<div class="shapediver-upload-footer">
				<button type="button" class="shapediver-btn-cancel">Cancel</button>
				<button type="button" class="shapediver-btn-upload" disabled>
					<span class="btn-text">Upload Image</span>
					<span class="btn-loading" style="display: none;">
						<span class="spinner"></span>
						Uploading...
					</span>
				</button>
			</div>
		</div>
	`;

	const tooltipEl = document.createElement("div");
	tooltipEl.className = "upload-tooltip-portal";
	tooltipEl.setAttribute("role", "tooltip");
	tooltipEl.innerHTML = `
		<strong>Flat Carved:</strong> Includes skin or deep cut art.<br><br>
		<strong>Shape Carved:</strong> A three-dimensional service, at an additional cost.<br><br>
		<strong>Laser:</strong> A great way to customize any jet black monument.<br><br>
		<strong>Porcelain Portrait:</strong> Images placed onto a porcelain shape and then affixed to the stone.<br><br>
		<em>Note: To prevent delays in fulfillment, please ensure images are suitable for their respective art types.</em>
	`;

	Object.assign(tooltipEl.style, {
		position:  "fixed",
		zIndex:    "2147483647",
		display:   "none",
		width:     "260px",
		pointerEvents: "none",
	});

	document.body.appendChild(tooltipEl);

	const MARGIN = 8;

	function positionTooltip(icon: HTMLElement): void {
		const rect     = icon.getBoundingClientRect();
		const ttHeight = tooltipEl.offsetHeight;

		if (rect.top >= ttHeight + MARGIN) {
			tooltipEl.style.top = `${rect.top - ttHeight - MARGIN}px`;
			tooltipEl.dataset.placement = "above";
		} else {
			tooltipEl.style.top = `${rect.bottom + MARGIN}px`;
			tooltipEl.dataset.placement = "below";
		}

		const centreLeft = rect.left + rect.width / 2 - 130;
		tooltipEl.style.left = `${Math.max(8, Math.min(centreLeft, window.innerWidth - 268))}px`;
	}

	const initObserver = new MutationObserver(() => {
		const icon = modal.querySelector<HTMLElement>(".upload-tooltip-icon");
		if (!icon) return;
		initObserver.disconnect();

		const show = (): void => {
			tooltipEl.style.display = "block";
			requestAnimationFrame(() => requestAnimationFrame(() => {
				positionTooltip(icon);
				tooltipEl.classList.add("upload-tooltip-portal--visible");
			}));
		};

		const hide = (): void => {
			tooltipEl.classList.remove("upload-tooltip-portal--visible");
			const onEnd = (): void => { tooltipEl.style.display = "none"; };
			tooltipEl.addEventListener("transitionend", onEnd, { once: true });
		};

		icon.addEventListener("mouseenter", show);
		icon.addEventListener("focus",      show);
		icon.addEventListener("mouseleave", hide);
		icon.addEventListener("blur",       hide);

		const cleanObserver = new MutationObserver(() => {
			if (!document.contains(modal)) {
				tooltipEl.remove();
				cleanObserver.disconnect();
			}
		});
		cleanObserver.observe(document.body, { childList: true, subtree: true });
	});

	initObserver.observe(document.body, { childList: true, subtree: true });

	return modal;
}

function setupImageUploadHandlers(
	modal: HTMLElement,
	resolve: (value: boolean) => void,
): void {
	const typeSelect = modal.querySelector("#image-type-select") as HTMLSelectElement;
	const fileInput = modal.querySelector("#image-file-input") as HTMLInputElement;
	const dropzone = modal.querySelector("#image-dropzone") as HTMLElement;
	const browseBtn = modal.querySelector(".upload-browse-btn") as HTMLButtonElement;
	const uploadBtn = modal.querySelector(".shapediver-btn-upload") as HTMLButtonElement;
	const cancelBtn = modal.querySelector(".shapediver-btn-cancel") as HTMLButtonElement;
	const closeBtn = modal.querySelector(".shapediver-upload-close") as HTMLButtonElement;
	const removeBtn = modal.querySelector(".upload-remove-btn") as HTMLButtonElement;
	const descriptionInput = modal.querySelector<HTMLTextAreaElement>("#image-description");
	const errorMessage = modal.querySelector(".upload-error-message") as HTMLElement;

	let selectedFile: File | null = null;

	browseBtn?.addEventListener("click", () => fileInput.click());

	fileInput?.addEventListener("change", (e) => {
		const target = e.target as HTMLInputElement;
		const file = target.files?.[0];
		if (file) handleFileSelect(file);
	});

	dropzone?.addEventListener("dragover", (e) => {
		e.preventDefault();
		dropzone.classList.add("dragover");
	});

	dropzone?.addEventListener("dragleave", () => {
		dropzone.classList.remove("dragover");
	});

	dropzone?.addEventListener("drop", (e) => {
		e.preventDefault();
		dropzone.classList.remove("dragover");
		const files = e.dataTransfer?.files;
		if (files && files.length > 0) handleFileSelect(files[0]);
	});

	removeBtn?.addEventListener("click", () => {
		selectedFile = null;
		fileInput.value = "";
		showFilePreview(false);
		validateForm();
	});

	typeSelect?.addEventListener("change", () => {
		errorMessage.style.display = "none";
		validateForm();
	});

	uploadBtn?.addEventListener("click", async () => {
		if (!selectedFile) { showError("Please select an image file"); return; }
		if (!typeSelect.value) { showError("Please select an image type"); return; }
		await handleUpload();
	});

	cancelBtn?.addEventListener("click", () => closeModal(false));
	closeBtn?.addEventListener("click", () => closeModal(false));

	modal.addEventListener("click", (e) => {
		if (e.target === modal) closeModal(false);
	});

	const escHandler = (e: KeyboardEvent) => {
		if (e.key === "Escape") closeModal(false);
	};
	document.addEventListener("keydown", escHandler);

	function handleFileSelect(file: File): void {
		const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
		if (!allowedTypes.includes(file.type)) {
			showError("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
			return;
		}
		if (file.size > 10 * 1024 * 1024) {
			showError("File size must be less than 10MB");
			return;
		}
		selectedFile = file;
		showFilePreview(true, file);
		validateForm();
		errorMessage.style.display = "none";
	}

	function showFilePreview(show: boolean, file?: File): void {
		const dropzoneContent = modal.querySelector(".upload-dropzone-content") as HTMLElement;
		const filePreview = modal.querySelector(".upload-file-preview") as HTMLElement;

		if (show && file) {
			const reader = new FileReader();
			reader.onload = (e) => {
				const previewImg = modal.querySelector(".upload-preview-image") as HTMLImageElement;
				const fileName = modal.querySelector(".upload-file-name") as HTMLElement;
				const fileSize = modal.querySelector(".upload-file-size") as HTMLElement;

				if (previewImg && e.target?.result) previewImg.src = e.target.result as string;
				if (fileName) fileName.textContent = file.name;
				if (fileSize) fileSize.textContent = formatBytes(file.size);
			};
			reader.readAsDataURL(file);

			dropzoneContent.style.display = "none";
			filePreview.style.display = "flex";
		} else {
			dropzoneContent.style.display = "flex";
			filePreview.style.display = "none";
		}
	}

	function validateForm(): void {
		uploadBtn.disabled = selectedFile === null || typeSelect.value === "";
	}

	function showError(message: string): void {
		errorMessage.textContent = message;
		errorMessage.style.display = "block";
	}

	async function handleUpload(): Promise<void> {
		if (!selectedFile) return;

		const btnText = uploadBtn.querySelector(".btn-text") as HTMLElement;
		const btnLoading = uploadBtn.querySelector(".btn-loading") as HTMLElement;

		uploadBtn.disabled = true;
		btnText.style.display = "none";
		btnLoading.style.display = "flex";

		try {
			const description = descriptionInput?.value ?? "";
			const result = await uploadFileToServer(selectedFile, typeSelect.value, description);

			if (result.success) {
				console.log("✅ Upload successful:", result.data);
				closeModal(true);
			} else {
				showError("Upload failed. Please try again.");
				uploadBtn.disabled = false;
				btnText.style.display = "inline";
				btnLoading.style.display = "none";
			}
		} catch (error) {
			console.error("❌ Upload error:", error);
			showError("Upload error: " + (error instanceof Error ? error.message : "Unknown error"));
			uploadBtn.disabled = false;
			btnText.style.display = "inline";
			btnLoading.style.display = "none";
		}
	}

	function closeModal(success: boolean): void {
		modal.remove();
		document.removeEventListener("keydown", escHandler);
		resolve(success);
	}

	function getTypeName(value: string): string {
		const types: Record<string, string> = {
			flat_carve: "Flat Carve",
			flat_shape_carve: "Flat / Shape Carve",
			flat_shape_carve_addl: "Flat / Shape Carve add'l",
			laser_scene: "Laser Scene",
			laser_spot: "Laser Spot",
		};
		return types[value] || value;
	}
}

async function uploadFileToServer(
	file: File,
	imageType: string,
	description: string = "",
): Promise<any> {
	const formData = new FormData();
	formData.append("file", file);
	formData.append("image_type", imageType);
	if (description) formData.append("description", description);

	const userId = (window as any).shapediverAjax?.isLoggedIn
		? await getCurrentUserId()
		: null;
	if (userId) formData.append("user_id", userId.toString());

	const apiUrl = `${window.location.origin}/wp-json/shapediver/v1/upload-images`;

	console.log("📤 Uploading to:", apiUrl, "| Type:", imageType, "| File:", file.name);

	const response = await fetch(apiUrl, {
		method: "POST",
		body: formData,
		credentials: "same-origin",
	});

	if (!response.ok) {
		const errorText = await response.text();
		throw new Error(`HTTP ${response.status}: ${errorText}`);
	}

	return await response.json();
}

async function getCurrentUserId(): Promise<number | null> {
	try {
		const response = await fetch(configuratorData.ajaxurl, {
			method: "POST",
			headers: {"Content-Type": "application/x-www-form-urlencoded"},
			credentials: "same-origin",
			body: new URLSearchParams({action: "get_user_profile"}),
		});
		const result = await response.json();
		if (result.success && result.data?.id) return parseInt(result.data.id);
	} catch (error) {
		console.error("Failed to get user ID:", error);
	}
	return null;
}

function formatBytes(bytes: number): string {
	if (bytes === 0) return "0 Bytes";
	const k = 1024;
	const sizes = ["Bytes", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
}

// ============================================================================
// CUSTOM — DOM READY & INITIALIZATION
// ============================================================================

(globalThis as {[key: string]: any}).openWarehousePopup = () =>
	console.log("Static warehouse popup not used");
(globalThis as {[key: string]: any}).closeWarehousePopup = () =>
	WarehouseManager.closePopup();
(globalThis as {[key: string]: any}).addWarehouseToCart = (_warehouse: string) =>
	console.log("Use dynamic add to cart");
(globalThis as {[key: string]: any}).openWarehousePopupDynamic = (
	warehouses: WarehouseData[],
	pricingParameters: Record<string, any>,
	modelStateId: string,
	productData?: ShapeDiverProductData | null,
) => WarehouseManager.openPopup(warehouses, pricingParameters, modelStateId, productData);

(globalThis as {[key: string]: any}).shapeDiverModalManager =
	ShapeDiverModalManager.getInstance();

// ── Pre-fetched family name cache ─────────────────────────────────────────
let cachedLatestFamilyName: string = "";
let familyNamePrefetchDone: boolean = false;

async function prefetchLatestFamilyName(): Promise<void> {
	try {
		const formData = new URLSearchParams();
		formData.append("action", "shapediver_get_latest_family_name");
		const nonce = (window as any).shapediverAjax?.nonce;
		if (nonce) formData.append("nonce", nonce);

		const r = await fetch("/wp-admin/admin-ajax.php", {
			method: "POST",
			credentials: "same-origin",
			headers: {"Content-Type": "application/x-www-form-urlencoded"},
			body: formData.toString(),
		});
		const result = await r.json();
		if (result.success && result.data?.designTitle) {
			cachedLatestFamilyName = result.data.designTitle;
		}
	} catch (err) {
		console.warn("⚠️ prefetchLatestFamilyName failed:", err);
	} finally {
		familyNamePrefetchDone = true;
	}
}

async function logProductVariantsToConsole(productId: string): Promise<void> {
	try {
		const response = await fetch(configuratorData.ajaxurl, {
			method: "POST",
			headers: {"Content-Type": "application/x-www-form-urlencoded"},
			credentials: "same-origin",
			body: new URLSearchParams({
				action: "get_product_variants_debug",
				product_id: productId,
			}),
		});
		const result = await response.json();
		if (!result.success) {
			console.error("❌ Failed to load variants:", result.data);
			return;
		}
		const rows = (result.data.variants || []).map((v: any) => ({
			variation_id: v.variation_id,
			sku: v.sku,
			price: v.price,
			regular_price: v.regular_price,
			setup_cost: v.setup_cost,
			in_stock: v.in_stock,
			...Object.fromEntries(
				Object.entries(v.attributes || {}).map(([k, val]) => [
					k.replace("attribute_", ""),
					val,
				]),
			),
		}));
		console.log(`📋 Variants for "${result.data.product_name}" (ID ${result.data.product_id}):`);
		console.table(rows);
	} catch (err) {
		console.error("❌ Error fetching variants:", err);
	}
}

// ============================================================================
// DOM READY — INITIALIZATION
// ============================================================================

document.addEventListener("DOMContentLoaded", function () {
	console.log("🚀 Initializing ShapeDiver configurator");

	SettingsManager.getInstance().load();
	ShapeDiverModalManager.getInstance();

	const productManager = ProductDataManager.getInstance();

	if (!configuratorData.isLoggedIn) {
		console.log("⚠️ User not logged in - setting up login button");
		const loginButton = document.getElementById("shapediver-login-button");
		if (loginButton) {
			loginButton.addEventListener("click", function (e) {
				e.preventDefault();
				const loginUrl =
					loginButton.getAttribute("data-login-url") || configuratorData.loginUrl;
				window.location.href = loginUrl;
			});
		}
		return;
	}

	// ── VIEW 3D MODEL button click handler ────────────────────────────────
	// NOTE: cart/checkout/order-context buttons are handled exclusively by
	// View3dButtonHandler.php. Do not re-add cart/checkout/order handling
	// here — extend View3dButtonHandler.php instead.

	// ── Find configurator button ──────────────────────────────────────────
	const configuratorButton = (
		document.getElementById("custom-sd-open-btn") ||
		document.getElementById("app-builder-open-configurator")
	) as HTMLButtonElement | null;

	if (configuratorButton) {
		const productIdAttr = configuratorButton.getAttribute("data-product-id");
		console.log("✅ Configurator button found:", configuratorButton.id, "| product-id:", productIdAttr);

		if (productIdAttr) {
			productManager.loadProductData(productIdAttr).then((productData) => {
				if (productData) {
					configuratorButton.removeAttribute("disabled");
					console.log("✅ Product data pre-loaded, button enabled");
				}
			});
		}

		// ── Debug: log all variants + attributes on click ──────────────────
		configuratorButton.addEventListener("click", () => {
			const productId = configuratorButton.getAttribute("data-product-id");
			if (productId) logProductVariantsToConsole(productId);
		});
	} else {
		console.warn("⚠️ Configurator button not found");
	}

	// ── Kick off prefetch immediately ─────────────────────────────────────
	prefetchLatestFamilyName();

	// ── MutationObserver: apply cached family name instantly on modal open ─
	const saveModal  = document.getElementById("shapediver-save-modal");
	const titleInput = document.getElementById("design-title") as HTMLInputElement | null;

	if (saveModal && titleInput) {
		const prefillObserver = new MutationObserver(() => {
			if (saveModal.style.display !== "flex") return;
			if (titleInput.value.trim() !== "") return;

			if (cachedLatestFamilyName) {
				titleInput.value = cachedLatestFamilyName;
				titleInput.dispatchEvent(new Event("input", {bubbles: true}));
				console.log("✅ Observer prefilled (from cache):", titleInput.value);
			} else if (!familyNamePrefetchDone) {
				const waitAndFill = async () => {
					while (!familyNamePrefetchDone) {
						await new Promise(res => setTimeout(res, 100));
					}
					if (
						saveModal.style.display === "flex" &&
						titleInput.value.trim() === "" &&
						cachedLatestFamilyName
					) {
						titleInput.value = cachedLatestFamilyName;
						titleInput.dispatchEvent(new Event("input", {bubbles: true}));
						console.log("✅ Observer prefilled (after prefetch):", titleInput.value);
					}
				};
				waitAndFill();
			}
		});

		prefillObserver.observe(saveModal, {
			attributes: true,
			attributeFilter: ["style"],
		});

		console.log("✅ Modal prefill observer attached");
	}
});

function shapediverOpenView3dIframe(url: string): void {
	const MODAL_ID = "sd-view3d-iframe-modal";
	let modal = document.getElementById(MODAL_ID);

	if (!modal) {
		modal = document.createElement("div");
		modal.id = MODAL_ID;
		modal.style.cssText = [
			"position:fixed",
			"inset:0",
			"z-index:9999999",
			"background:rgba(0,0,0,0.85)",
			"display:none",
			"align-items:center",
			"justify-content:center",
			"padding:16px",
		].join(";");

		modal.innerHTML = `
			<div style="
				position:relative;
				width:100%;
				max-width:1200px;
				height:90vh;
				background:#000;
				border-radius:8px;
				overflow:hidden;
			">
				<button id="sd-view3d-close-btn" style="
					position:absolute;
					top:10px;
					right:12px;
					z-index:10;
					background:rgba(0,0,0,0.55);
					border:none;
					color:#fff;
					font-size:28px;
					line-height:1;
					width:38px;
					height:38px;
					border-radius:50%;
					cursor:pointer;
					display:flex;
					align-items:center;
					justify-content:center;
					backdrop-filter:blur(4px);
				" aria-label="Close">&times;</button>
				<iframe id="sd-view3d-iframe"
					src=""
					allow="camera;microphone;xr-spatial-tracking"
					allowfullscreen
					style="width:100%;height:100%;border:none;display:block;background:#fff;"
				></iframe>
			</div>`;

		document.body.appendChild(modal);

		document.getElementById("sd-view3d-close-btn")!
			.addEventListener("click", (e: MouseEvent) => {
				e.stopPropagation();
				shapediverCloseView3dIframe();
			});

		modal.addEventListener("click", (e: MouseEvent) => {
			if (e.target === modal) shapediverCloseView3dIframe();
		});

		document.addEventListener("keydown", (e: KeyboardEvent) => {
			if (e.key === "Escape" && modal!.style.display === "flex") {
				shapediverCloseView3dIframe();
			}
		});
	}

	const iframe = document.getElementById("sd-view3d-iframe") as HTMLIFrameElement;
	if (iframe) iframe.src = url;
	modal.style.display = "flex";
	document.body.style.overflow = "hidden";
}

function shapediverCloseView3dIframe(): void {
	const modal = document.getElementById("sd-view3d-iframe-modal");
	if (!modal) return;
	const iframe = document.getElementById("sd-view3d-iframe") as HTMLIFrameElement;
	if (iframe) iframe.src = "";
	modal.style.display = "none";
	document.body.style.overflow = "";
}

function showFamilyNameBeforeOpen(
	existingStateId: string = "",
	prefillTitleOverride: string = "",
): Promise<string | null> {
	return Promise.resolve(prefillTitleOverride.trim() || null);
}

(globalThis as any).showFamilyNameBeforeOpen = showFamilyNameBeforeOpen;


// ============================================================================
// TEMPORARY — fill RATE = PRICE / QTY in the "Product Cost Breakup" table.
// The table is rendered by another plugin that leaves RATE at $0.00; this
// rewrites the RATE cell on screen until that renderer is fixed.
// ============================================================================
(function fillRateColumn() {
	const parseMoney = (t: string) => parseFloat((t || "").replace(/[^0-9.\-]/g, "")) || 0;

	const fixTables = () => {
		document.querySelectorAll("table").forEach((table) => {
			const headers = Array.from(table.querySelectorAll("thead th, tr:first-child th"))
				.map((th) => (th.textContent || "").trim().toUpperCase());
			const rateIdx = headers.indexOf("RATE");
			const priceIdx = headers.indexOf("PRICE");
			const qtyIdx = headers.indexOf("QTY");
			if (rateIdx < 0 || priceIdx < 0) return;

			table.querySelectorAll("tbody tr").forEach((row) => {
				const cells = row.querySelectorAll("td");
				const rateCell = cells[rateIdx] as HTMLElement | undefined;
				const priceCell = cells[priceIdx] as HTMLElement | undefined;
				if (!rateCell || !priceCell) return;
				if (parseMoney(rateCell.textContent || "") > 0) return; // already has a rate

				const price = parseMoney(priceCell.textContent || "");
				const qty = qtyIdx >= 0 ? Math.max(1, parseMoney(cells[qtyIdx]?.textContent || "1")) : 1;
				const symbol = ((priceCell.textContent || "").trim().match(/^[^\d\-]*/) || ["$"])[0] || "$";
				const newText = symbol + (price / qty).toLocaleString("en-US", {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
				});

				// Only write when it changes, so the observer doesn't loop
				if ((rateCell.textContent || "").trim() !== newText) {
					rateCell.textContent = newText;
				}
			});
		});
	};

	const start = () => {
		fixTables();
		// The popup is inserted after page load, so re-run whenever the page changes
		new MutationObserver(() => fixTables()).observe(document.body, {childList: true, subtree: true});
	};

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", start);
	} else {
		start();
	}
})();